import { Bot, BrainCircuit, CheckCircle, ChevronRight, Clock, Download, ExternalLink, Flag, Globe2, Info, Loader2, Monitor, RefreshCw, Search, Send, Smartphone, Sparkles, Star, Tablet, ThumbsDown, ThumbsUp, Tv } from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { deleteClientHito, downloadSeoAudit, fetchClientDetail, fetchConversationBots, fetchSocialYoutube, fetchUrlDetail, runGertrudisPaid, runGertrudisSeo, runPageSpeed, submitGertrudisFeedback, triggerGertrudisSeoAudit } from "../api";
import { Markdown } from "../lib/markdown";
import { UrlContextBar } from "./UrlContextBar";
import { usePageHeaderSlots } from "../context/PageHeaderSlotContext";
import { useDashboard } from "../context/DashboardContext";
import { currency } from "../lib/format";
import { ChartTooltip, type ChartHitoItem } from "./common/ChartTooltip";
import { RegisterHitoModal } from "./RegisterHitoModal";
import type {
  AuditDetailPayload,
  ClientDetailPayloadMap,
  ClientDetailSection,
  DataRecord,
  GertrudisAuditPayload,
  GertrudisUsedData,
  GapsDetailPayload,
  PaidDetailPayload,
  SeoDetailPayload,
  SeoAuditSnapshot,
  SemrushExplorePayload,
  TransparencyDetailPayload,
  UrlDetailPayload,
  WhatsappDetailPayload,
  BotMetadata,
} from "../types";
import { ClientConversationsTab } from "./conversations/ClientConversationsTab";
import { SemrushSiteAuditCards } from "./SemrushSiteAuditCards";
import { GeoTrackingPanel } from "./GeoTrackingPanel";
import { SiteAuditPageIssuesPanel } from "./SiteAuditPageIssuesPanel";
import {
  PRESET_LABELS,
  readStoredRange,
  rangeToWindow,
  storeRange,
  todaySantiago,
  type RangePreset,
  type SocialRange,
} from "./social/socialRange";
import { AudienciaYoutube } from "./social/youtube/AudienciaYoutube";
import { useDocumentTheme } from "./social/useDocumentTheme";

type PrimaryTab = "summary" | "seo" | "paid" | "whatsapp" | "audiencias";
type SeoTab = "overview" | "opportunities" | "explore";
type PaidTab = "pacing" | "transparency";

const primaryTabs: Array<{ id: PrimaryTab; label: string }> = [
  { id: "summary", label: "Resumen" },
  { id: "seo", label: "SEO" },
  { id: "paid", label: "Paid" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "audiencias", label: "Audiencias" },
];

function number(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function text(value: unknown, fallback = "—"): string {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

function record(value: unknown): DataRecord {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as DataRecord) : {};
}

function rows(value: unknown): DataRecord[] {
  return Array.isArray(value) ? value.filter((item): item is DataRecord => Boolean(item) && typeof item === "object") : [];
}

function normalizedUrlKey(value: string): string {
  return value.trim()
    .replace(/^sc-domain:/i, "")
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/[?#].*$/, "")
    .replace(/\/+$/, "")
    .toLowerCase();
}

function pickDefaultUrl(urls: string[], preferred?: string): string {
  if (!urls.length) return "";
  if (preferred) {
    const preferredKey = normalizedUrlKey(preferred);
    const match = urls.find((url) => normalizedUrlKey(url) === preferredKey);
    if (match) return match;
  }
  const homes = urls.filter((url) => !normalizedUrlKey(url).includes("/"));
  return [...homes].sort((left, right) => left.length - right.length)[0] ?? urls[0];
}

function formatValue(value: unknown, format?: "currency" | "percent" | "decimal" | "integer"): string {
  if (value === null || value === undefined || value === "") return "—";
  if (format === "currency") return currency.format(number(value));
  if (format === "percent") return `${(number(value) * (Math.abs(number(value)) <= 1 ? 100 : 1)).toFixed(1)}%`;
  if (format === "decimal") return number(value).toLocaleString("es-CL", { maximumFractionDigits: 2 });
  if (format === "integer") return Math.round(number(value)).toLocaleString("es-CL");
  if (typeof value === "number") return value.toLocaleString("es-CL", { maximumFractionDigits: 2 });
  return String(value);
}

function statusTone(status: unknown): string | undefined {
  const s = text(status).toUpperCase().trim();
  if (s === "OVER" || s === "DANGER") return "accent-danger";
  if (s === "AT_RISK" || s === "WARNING" || s === "EN RIESGO") return "accent-warning";
  if (s === "OK" || s === "GOOD") return "accent-good";
  return undefined;
}


interface Column {
  key: string;
  label: string;
  format?: "currency" | "percent" | "decimal" | "integer";
  tone?: (row: DataRecord) => string | undefined;
  render?: (row: DataRecord) => ReactNode;
}

function DataTable({ data, columns, empty = "Sin datos disponibles para este bloque." }: { data: DataRecord[]; columns: Column[]; empty?: string }) {
  if (!data.length) return <p className="empty-table">{empty}</p>;
  return (
    <div className="table-scroll detail-table-scroll">
      <table className="usage-table detail-table">
        <thead>
          <tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={`${text(row[columns[0]?.key])}-${index}`}>
              {columns.map((column, cellIndex) => {
                const rawVal = row[column.key];
                const displayVal = column.render ? column.render(row) : formatValue(rawVal, column.format);
                const titleVal = typeof rawVal === "string" || typeof rawVal === "number" ? String(rawVal) : undefined;
                return (
                  <td
                    className={cellIndex === 0 ? "primary-cell" : column.tone?.(row)}
                    key={column.key}
                    title={titleVal}
                  >
                    {displayVal}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Sparkline({ points, tone }: { points: number[]; tone?: string }) {
  const width = 100;
  const height = 22;
  const max = Math.max(...points, 0);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const step = width / Math.max(points.length - 1, 1);
  const path = points
    .map((value, index) => `${index ? "L" : "M"}${(index * step).toFixed(1)},${(height - ((value - min) / range) * height).toFixed(1)}`)
    .join(" ");
  const color = tone === "danger" ? "var(--red)" : tone === "good" ? "var(--green)" : "var(--blue)";
  return (
    <svg className="detail-metric-spark" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** "reservas" → "reserva", "cotizaciones" → "cotización", "sesiones" → "sesión". */
function singularize(word: string): string {
  if (/ciones$/i.test(word)) return word.replace(/ciones$/i, "ción");
  if (/siones$/i.test(word)) return word.replace(/siones$/i, "sión");
  if (/s$/i.test(word)) return word.slice(0, -1);
  return word;
}

const UpdatingContext = createContext<boolean>(false);
const useUpdating = () => useContext(UpdatingContext);

function DetailMetric({
  label,
  value,
  note,
  tone,
  spark,
  isUpdating: explicitUpdating,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: string;
  spark?: number[];
  isUpdating?: boolean;
}) {
  const contextUpdating = useUpdating();
  const isUpdating = explicitUpdating ?? contextUpdating;
  return (
    <article className={`detail-metric ${tone ? `tone-${tone}` : ""} ${isUpdating ? "opacity-75 transition-opacity" : ""}`}>
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "4px" }}>
        {label}
        {isUpdating && <Loader2 className="animate-spin" size={12} style={{ color: "var(--accent, #38bdf8)" }} />}
      </span>
      <strong>{value}</strong>
      {note && <small>{note}</small>}
      {spark && spark.length > 1 && <Sparkline points={spark} tone={tone} />}
    </article>
  );
}

function niceAxisMax(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function compactNumber(value: number): string {
  return new Intl.NumberFormat("es-CL", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

function compactCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function useMeasuredChartWidth(fallbackWidth: number = 720) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallbackWidth);
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = () => {
      const clientW = node.clientWidth;
      if (clientW > 0) {
        setWidth(Math.floor(clientW));
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [fallbackWidth]);
  return { containerRef, width };
}

function shortDateLabel(raw: unknown): string {
  const iso = text(raw, "");
  if (!iso || iso === "NaT" || iso === "None" || iso === "null") return "—";
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "—";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat("es-CL", { day: "2-digit", month: "short", timeZone: "UTC" })
    .format(date)
    .replace(".", "");
}

// Paleta categórica fija, orden fijo -- nunca se reasigna por índice de filtro
// (ver skill dataviz: "color follows the entity, never its rank").
const CATEGORICAL_HUES = ["#6f8cff", "#ad7bff", "#4fd1c5", "#f6ad55", "#f56565", "#38bdf8"];
const MAX_SERIES_BEFORE_OTROS = 5;

const ACTO_DE_HABLA_LABELS: Record<string, string> = {
  pregunta: "Pregunta", reclamo: "Reclamo", objecion_precio: "Objeción precio",
  critica: "Crítica", elogio: "Elogio", solicitud_contacto: "Solicitud contacto",
  reaccion: "Reacción", spam: "Spam",
};

const MEDIA_TYPE_LABELS: Record<string, string> = {
  HISTORIA: "Historia",
  REEL: "Reel",
  CARRUSEL: "Carrusel",
  VIDEO: "Video",
  IMAGEN: "Imagen",
  ENLACE: "Enlace",
  TEXTO: "Solo texto",
  OTRO: "Otro",
};

function bySnapshotDate(data: DataRecord[]): DataRecord[] {
  return [...data].sort((left, right) => text(left.snapshot_date).localeCompare(text(right.snapshot_date)));
}

function VisibilidadShareChart({ data }: { data: DataRecord[] }) {
  const clean = bySnapshotDate(data);
  const minWidth = Math.max(680, Math.min(clean.length, 60) * 80 + 140);
  const { containerRef, width } = useMeasuredChartWidth(minWidth);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  if (!clean.length) return <p className="chart-empty">Todavía hay un solo snapshot -- vuelve a cargar Semrush para ver la evolución.</p>;

  const activeIdx = hoveredIdx !== null ? hoveredIdx : clean.length - 1;
  const activeRow = clean[activeIdx] || clean[clean.length - 1];

  const height = 290;
  const margin = { top: 32, right: 30, bottom: 44, left: 60 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  const totals = clean.map((row) => number(row.kw_total));
  const yMax = niceAxisMax(Math.max(1, ...totals) * 1.15);
  const ticks = [0, 0.25, 0.5, 0.75, 1];

  const bands: Array<{ key: string; label: string; color: string }> = [
    { key: "kw_top3", label: "Top 3", color: "#10b981" },
    { key: "kw_top10", label: "Top 4-10", color: "#3b82f6" },
    { key: "kw_top20", label: "Top 11-20", color: "#f59e0b" },
    { key: "kw_mas_20", label: "21+", color: "#818cf8" },
  ];

  const colStep = clean.length === 1 ? plotWidth : plotWidth / clean.length;
  const barWidth = Math.min(84, Math.max(38, colStep * 0.55));
  const xCenter = (index: number) => margin.left + (index + 0.5) * colStep;

  const activeTotal = Math.max(1, number(activeRow.kw_total));

  return (
    <div className="seo-chart-wrap" style={{ position: "relative" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "10px", padding: "10px 14px", background: "rgba(255,255,255,0.03)", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.07)", marginBottom: "14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>
            Snapshot: <strong style={{ color: "var(--text-primary)" }}>{text(activeRow.snapshot_date)}</strong>
          </span>
          <span style={{ fontSize: "12px", background: "rgba(255,255,255,0.08)", padding: "2px 8px", borderRadius: "12px", color: "var(--text-primary)", fontWeight: 600 }}>
            {formatValue(activeRow.kw_total, "integer")} Keywords
          </span>
          {activeRow.traffic_total_est ? (
            <span style={{ fontSize: "12px", background: "rgba(16,185,129,0.12)", color: "#10b981", padding: "2px 8px", borderRadius: "12px", fontWeight: 500 }}>
              Tráfico est: {formatValue(activeRow.traffic_total_est, "integer")}
            </span>
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          {bands.map((band) => {
            const count = number(activeRow[band.key]);
            const pct = ((count / activeTotal) * 100).toFixed(1);
            return (
              <span key={band.key} style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px" }}>
                <span style={{ width: "9px", height: "9px", borderRadius: "50%", background: band.color, display: "inline-block" }} />
                <span style={{ color: "var(--text-muted)" }}>{band.label}:</span>
                <strong style={{ color: "var(--text-primary)" }}>{formatValue(count, "integer")}</strong>
                <span style={{ color: "var(--text-muted)", fontSize: "11px" }}>({pct}%)</span>
              </span>
            );
          })}
        </div>
      </div>

      <div className="time-chart-scroll" ref={containerRef}>
        <svg className="time-chart" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Distribución de keywords por rango de posición, evolutivo" onMouseLeave={() => setHoveredIdx(null)}>
          {ticks.map((ratio) => {
            const y = margin.top + plotHeight - ratio * plotHeight;
            return (
              <g key={ratio}>
                <line className="chart-grid-line" x1={margin.left} x2={width - margin.right} y1={y} y2={y} stroke="rgba(255,255,255,0.07)" strokeDasharray="3 3" />
                <text className="chart-axis-label" x={margin.left - 10} y={y} textAnchor="end" dominantBaseline="middle" fill="var(--text-muted)" fontSize={11}>
                  {compactNumber(yMax * ratio)}
                </text>
              </g>
            );
          })}
          <text className="chart-axis-title" x={margin.left} y={16} fill="var(--text-muted)" fontSize={11} fontWeight={600} letterSpacing="0.05em">
            KEYWORDS
          </text>

          {clean.map((row, colIdx) => {
            const cx = xCenter(colIdx);
            const xLeft = cx - barWidth / 2;
            const isHovered = hoveredIdx === colIdx || (hoveredIdx === null && colIdx === clean.length - 1);

            let curCum = 0;
            const segments = bands.map((band) => {
              const val = number(row[band.key]);
              curCum += val;
              const h = (val / yMax) * plotHeight;
              const y = margin.top + plotHeight - (curCum / yMax) * plotHeight;
              return { ...band, val, y, h };
            });

            const totalKw = number(row.kw_total);
            const totalY = margin.top + plotHeight - (curCum / yMax) * plotHeight - 8;

            return (
              <g key={text(row.snapshot_date)} style={{ cursor: "pointer" }} onMouseEnter={() => setHoveredIdx(colIdx)}>
                {isHovered && (
                  <rect
                    x={cx - colStep / 2 + 4}
                    y={margin.top}
                    width={colStep - 8}
                    height={plotHeight}
                    fill="rgba(255,255,255,0.03)"
                    rx={6}
                  />
                )}

                {segments.map((seg, sIdx) => {
                  const isTop = sIdx === segments.length - 1;
                  const isBottom = sIdx === 0;
                  return (
                    <g key={seg.key}>
                      <rect
                        x={xLeft}
                        y={seg.y}
                        width={barWidth}
                        height={Math.max(0, seg.h)}
                        fill={seg.color}
                        fillOpacity={isHovered ? 0.95 : 0.75}
                        rx={isTop ? 4 : isBottom ? 4 : 0}
                        stroke={isHovered ? "#ffffff" : "rgba(0,0,0,0.3)"}
                        strokeWidth={isHovered ? 1.5 : 0.5}
                      />
                      {seg.h >= 18 && (
                        <text
                          x={cx}
                          y={seg.y + seg.h / 2}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#ffffff"
                          fontSize={11}
                          fontWeight={600}
                          style={{ pointerEvents: "none", textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}
                        >
                          {compactNumber(seg.val)}
                        </text>
                      )}
                    </g>
                  );
                })}

                <text
                  x={cx}
                  y={totalY}
                  textAnchor="middle"
                  fill={isHovered ? "var(--text-primary)" : "var(--text-muted)"}
                  fontSize={12}
                  fontWeight={isHovered ? 700 : 500}
                >
                  {formatValue(totalKw, "integer")}
                </text>

                <text
                  x={cx}
                  y={height - 14}
                  textAnchor="middle"
                  fill={isHovered ? "var(--text-primary)" : "var(--text-muted)"}
                  fontSize={12}
                  fontWeight={isHovered ? 600 : 400}
                >
                  {shortDateLabel(row.snapshot_date)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", padding: "10px 14px", background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid var(--line)", marginTop: "12px", fontSize: "11px", color: "var(--muted)" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#10b981", display: "inline-block" }} />
          <strong style={{ color: "var(--text-primary)" }}>Top 3:</strong> Posiciones 1 a 3 (máxima visibilidad y clics directos)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#3b82f6", display: "inline-block" }} />
          <strong style={{ color: "var(--text-primary)" }}>Top 4-10:</strong> Primera página de Google
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#f59e0b", display: "inline-block" }} />
          <strong style={{ color: "var(--text-primary)" }}>Top 11-20:</strong> Segunda página (Striking distance)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#818cf8", display: "inline-block" }} />
          <strong style={{ color: "var(--text-primary)" }}>21+:</strong> Posición 21 o superior
        </span>
      </div>
    </div>
  );
}

function SmallLineChart({ data, valueKey, label, yDomainMax, color, format = "integer" }: {
  data: DataRecord[]; valueKey: string; label: string; yDomainMax?: number; color: string; format?: "integer" | "decimal";
}) {
  const clean = bySnapshotDate(data);
  const minWidth = Math.max(340, Math.min(clean.length, 40) * 34 + 80);
  const { containerRef, width } = useMeasuredChartWidth(minWidth);
  if (!clean.length) return <p className="chart-empty">Sin datos todavía.</p>;

  const height = 190;
  const margin = { top: 16, right: 16, bottom: 34, left: 44 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const values = clean.map((row) => number(row[valueKey]));
  const yMax = yDomainMax ?? niceAxisMax(Math.max(1, ...values) * 1.15);
  const ticks = Array.from({ length: 4 }, (_, index) => index / 3);
  const xAt = (index: number) => clean.length === 1 ? margin.left + plotWidth / 2 : margin.left + plotWidth * index / (clean.length - 1);
  const yAt = (value: number) => margin.top + plotHeight - Math.min(value, yMax) / yMax * plotHeight;
  const path = clean.map((row, index) => `${index === 0 ? "M" : "L"} ${xAt(index)} ${yAt(number(row[valueKey]))}`).join(" ");
  const labelStep = Math.max(1, Math.ceil(clean.length / 5));

  return <div className="seo-chart-wrap compact-chart">
    <div className="chart-mini-title">{label}</div>
    <div className="time-chart-scroll" ref={containerRef}>
      <svg className="time-chart" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label}, evolutivo`}>
        {ticks.map((ratio) => {
          const y = margin.top + plotHeight - ratio * plotHeight;
          return <g key={ratio}>
            <line className="chart-grid-line" x1={margin.left} x2={width - margin.right} y1={y} y2={y} />
            <text className="chart-axis-label" x={margin.left - 8} y={y} textAnchor="end" dominantBaseline="middle">{formatValue(yMax * ratio, format)}</text>
          </g>;
        })}
        <path d={path} fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {clean.map((row, index) => {
          const showLabel = clean.length <= 8 || index === 0 || index === clean.length - 1 || index % labelStep === 0;
          return <g key={text(row.snapshot_date)}>
            <circle cx={xAt(index)} cy={yAt(number(row[valueKey]))} r={3} fill={color} stroke="var(--surface)" strokeWidth={1.5} vectorEffect="non-scaling-stroke">
              <title>{`${text(row.snapshot_date)} · ${formatValue(row[valueKey], format)}`}</title>
            </circle>
            {showLabel && <text className="chart-x-label" x={xAt(index)} y={height - 12} textAnchor="middle">{shortDateLabel(row.snapshot_date)}</text>}
          </g>;
        })}
      </svg>
    </div>
  </div>;
}

function SmallBarChart({ data, series, format = "integer" }: {
  data: DataRecord[]; series: Array<{ key: string; label: string; color: string }>; format?: "integer" | "decimal";
}) {
  const clean = bySnapshotDate(data).filter((row) => series.some((serie) => row[serie.key] !== null && row[serie.key] !== undefined));
  const minWidth = Math.max(340, Math.min(clean.length, 30) * 44 + 80);
  const { containerRef, width } = useMeasuredChartWidth(minWidth);
  if (!clean.length) return <p className="chart-empty">Sin datos todavía.</p>;

  const height = 190;
  const margin = { top: 16, right: 16, bottom: 34, left: 44 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const maxTotal = Math.max(1, ...clean.map((row) => series.reduce((sum, serie) => sum + number(row[serie.key]), 0)));
  const yMax = niceAxisMax(maxTotal * 1.15);
  const ticks = Array.from({ length: 4 }, (_, index) => index / 3);
  const step = plotWidth / Math.max(clean.length, 1);
  const xAt = (index: number) => margin.left + step * (index + 0.5);
  const yAt = (value: number) => margin.top + plotHeight - value / yMax * plotHeight;
  const barWidth = Math.min(28, Math.max(10, step * 0.5));
  const labelStep = Math.max(1, Math.ceil(clean.length / 6));

  return <div className="seo-chart-wrap compact-chart">
    <div className="chart-mini-legend">
      {series.map((serie) => <span key={serie.key}><i style={{ background: serie.color }} />{serie.label}</span>)}
    </div>
    <div className="time-chart-scroll" ref={containerRef}>
      <svg className="time-chart" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Barras apiladas">
        {ticks.map((ratio) => {
          const y = margin.top + plotHeight - ratio * plotHeight;
          return <g key={ratio}>
            <line className="chart-grid-line" x1={margin.left} x2={width - margin.right} y1={y} y2={y} />
            <text className="chart-axis-label" x={margin.left - 8} y={y} textAnchor="end" dominantBaseline="middle">{formatValue(yMax * ratio, format)}</text>
          </g>;
        })}
        {clean.map((row, index) => {
          let cum = 0;
          const x = xAt(index);
          const showLabel = clean.length <= 8 || index === 0 || index === clean.length - 1 || index % labelStep === 0;
          return <g key={text(row.snapshot_date)}>
            {series.map((serie) => {
              const value = number(row[serie.key]);
              const yTop = yAt(cum + value);
              const yBottom = yAt(cum);
              cum += value;
              return <rect key={serie.key} x={x - barWidth / 2} y={yTop} width={barWidth} height={Math.max(0, yBottom - yTop)} fill={serie.color} rx={3}>
                <title>{`${text(row.snapshot_date)} · ${serie.label}: ${formatValue(value, format)}`}</title>
              </rect>;
            })}
            {showLabel && <text className="chart-x-label" x={x} y={height - 12} textAnchor="middle">{shortDateLabel(row.snapshot_date)}</text>}
          </g>;
        })}
      </svg>
    </div>
  </div>;
}

function groupSeriesByEntity(data: DataRecord[], entityKey: string, valueKey: string): Array<{ entity: string; points: DataRecord[]; latestValue: number }> {
  const byEntity = new Map<string, DataRecord[]>();
  bySnapshotDate(data).forEach((row) => {
    const entity = text(row[entityKey], "");
    if (!entity) return;
    const list = byEntity.get(entity) ?? [];
    list.push(row);
    byEntity.set(entity, list);
  });
  return [...byEntity.entries()]
    .map(([entity, points]) => ({ entity, points, latestValue: number(points[points.length - 1]?.[valueKey]) }))
    .sort((left, right) => right.latestValue - left.latestValue);
}

function KeywordGapTrendChart({ data }: { data: DataRecord[] }) {
  const series = groupSeriesByEntity(data, "competitor_domain", "volumen_faltante").slice(0, MAX_SERIES_BEFORE_OTROS);
  const allDates = [...new Set(bySnapshotDate(data).map((row) => text(row.snapshot_date)))];
  const minWidth = Math.max(600, Math.min(allDates.length, 40) * 36 + 120);
  const { containerRef, width } = useMeasuredChartWidth(minWidth);
  if (!series.length || allDates.length === 0) return <p className="chart-empty">Sin Keyword Gap cargado todavía para este cliente.</p>;

  if (allDates.length === 1) {
    const singleDate = allDates[0];
    const maxVol = Math.max(1, ...series.map((s) => s.latestValue));
    return (
      <div className="seo-chart-wrap" style={{ padding: "8px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          <span style={{ fontSize: "12px", color: "var(--text-muted, #94a3b8)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
            Snapshot inicial: {shortDateLabel(singleDate)}
          </span>
          <span style={{ fontSize: "12px", color: "var(--text-secondary, #cbd5e1)", background: "rgba(255,255,255,0.04)", padding: "4px 10px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.08)" }}>
            Tendencia evolutiva activa al registrar 2 o más cargas periódicas
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {series.map((serie, index) => {
            const point = serie.points[0];
            const vol = number(point?.volumen_faltante ?? serie.latestValue);
            const kws = number(point?.keywords_faltantes ?? 0);
            const totalKws = number(point?.keywords_comparadas ?? 0);
            const pct = Math.min(100, Math.max(4, Math.round((vol / maxVol) * 100)));
            const color = CATEGORICAL_HUES[index % CATEGORICAL_HUES.length];
            return (
              <div key={serie.entity} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "8px", padding: "12px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "8px", flexWrap: "wrap", gap: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: color, display: "inline-block" }} />
                    <span style={{ fontWeight: 600, fontSize: "14px", color: "var(--text, #f8fafc)" }}>{serie.entity}</span>
                  </div>
                  <div style={{ display: "flex", gap: "16px", fontSize: "13px" }}>
                    <span>
                      <strong style={{ color: color }}>{formatValue(vol, "integer")}</strong> <span style={{ color: "var(--text-muted, #94a3b8)" }}>volumen de brecha</span>
                    </span>
                    <span style={{ color: "var(--text-muted, #94a3b8)" }}>
                      <strong style={{ color: "var(--text, #f8fafc)" }}>{formatValue(kws, "integer")}</strong> {totalKws > 0 ? `/ ${formatValue(totalKws, "integer")}` : ""} keywords
                    </span>
                  </div>
                </div>
                <div style={{ width: "100%", height: "8px", backgroundColor: "rgba(255,255,255,0.06)", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", backgroundColor: color, borderRadius: "4px" }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const height = 260;
  const margin = { top: 18, right: 20, bottom: 44, left: 66 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const yMax = niceAxisMax(Math.max(1, ...series.flatMap((serie) => serie.points.map((point) => number(point.volumen_faltante)))) * 1.1);
  const ticks = Array.from({ length: 5 }, (_, index) => index / 4);
  const xAt = (index: number) => allDates.length === 1 ? margin.left + plotWidth / 2 : margin.left + plotWidth * index / (allDates.length - 1);
  const yAt = (value: number) => margin.top + plotHeight - value / yMax * plotHeight;
  const labelStep = Math.max(1, Math.ceil(allDates.length / 8));

  return <div className="seo-chart-wrap">
    <div className="seo-chart-legend" aria-label="Leyenda de competidores">
      {series.map((serie, index) => <span key={serie.entity}><i style={{ borderColor: CATEGORICAL_HUES[index] }} />{serie.entity}</span>)}
    </div>
    <div className="time-chart-scroll" ref={containerRef}>
      <svg className="time-chart" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Volumen de búsqueda faltante vs. competidores, evolutivo">
        {ticks.map((ratio) => {
          const y = margin.top + plotHeight - ratio * plotHeight;
          return <g key={ratio}>
            <line className="chart-grid-line" x1={margin.left} x2={width - margin.right} y1={y} y2={y} />
            <text className="chart-axis-label" x={margin.left - 10} y={y} textAnchor="end" dominantBaseline="middle">{compactNumber(yMax * ratio)}</text>
          </g>;
        })}
        <text className="chart-axis-title" x={12} y={13}>VOLUMEN FALTANTE</text>
        {series.map((serie, seriesIndex) => {
          const byDate = new Map(serie.points.map((point) => [text(point.snapshot_date), point]));
          const path = allDates
            .map((date, index) => {
              const point = byDate.get(date);
              if (!point) return null;
              return `${index === 0 ? "M" : "L"} ${xAt(index)} ${yAt(number(point.volumen_faltante))}`;
            })
            .filter((segment): segment is string => segment !== null)
            .join(" ");
          const color = CATEGORICAL_HUES[seriesIndex % CATEGORICAL_HUES.length];
          return <g key={serie.entity}>
            <path d={path} fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            {serie.points.map((point) => {
              const index = allDates.indexOf(text(point.snapshot_date));
              return <circle key={`${serie.entity}-${text(point.snapshot_date)}`} cx={xAt(index)} cy={yAt(number(point.volumen_faltante))} r={3} fill={color} stroke="var(--surface)" strokeWidth={1.5} vectorEffect="non-scaling-stroke">
                <title>{`${serie.entity} · ${text(point.snapshot_date)} · ${formatValue(point.volumen_faltante, "integer")} vol. faltante (${formatValue(point.keywords_faltantes, "integer")} keywords)`}</title>
              </circle>;
            })}
          </g>;
        })}
        {allDates.map((date, index) => {
          const showLabel = allDates.length <= 12 || index === 0 || index === allDates.length - 1 || index % labelStep === 0;
          return showLabel ? <text key={date} className="chart-x-label" x={xAt(index)} y={height - 14} textAnchor="middle">{shortDateLabel(date)}</text> : null;
        })}
      </svg>
    </div>
  </div>;
}

function mapActionsToDataPoints(
  actions: DataRecord[],
  data: DataRecord[]
): Array<DataRecord & { idx: number; dateStr: string }> {
  if (!actions.length || !data.length) return [];
  const dateMap = new Map<string, number>();
  data.forEach((row, idx) => {
    const d = String(row.date || row.snapshot_date || "").slice(0, 10);
    if (d && !dateMap.has(d)) dateMap.set(d, idx);
  });

  return actions
    .filter((a) => a.executed_at || a.date)
    .map((a) => {
      const d = String(a.executed_at || a.date || "").slice(0, 10);
      let idx = dateMap.get(d);
      if (idx === undefined) {
        const targetMs = new Date(d).getTime();
        let bestDiff = Infinity;
        let bestIdx = -1;
        data.forEach((row, i) => {
          const rowDateStr = String(row.date || row.snapshot_date || "").slice(0, 10);
          if (!rowDateStr) return;
          const rowMs = new Date(rowDateStr).getTime();
          const diff = Math.abs(rowMs - targetMs);
          if (diff < bestDiff) {
            bestDiff = diff;
            bestIdx = i;
          }
        });
        if (bestIdx !== -1 && bestDiff <= 14 * 86400000) {
          idx = bestIdx;
        }
      }
      return { ...a, idx: idx !== undefined ? idx : -1, dateStr: d };
    })
    .filter((a): a is DataRecord & { idx: number; dateStr: string } => a.idx >= 0);
}

function aggregatePaidDaily(data: DataRecord[], start?: string, end?: string): DataRecord[] {
  const byDate = new Map<string, { spend: number; budget: number; budgetAccounts: Set<string> }>();
  data.forEach((row, index) => {
    const date = text(row.date, "").slice(0, 10);
    if (!date) return;
    const current = byDate.get(date) ?? { spend: 0, budget: 0, budgetAccounts: new Set<string>() };
    current.spend += number(row.spend_daily);
    const account = `${text(row.platform, "unknown")}|${text(row.account_id ?? row.account_name, `row-${index}`)}`;
    if (!current.budgetAccounts.has(account)) {
      current.budget += number(row.budget_daily);
      current.budgetAccounts.add(account);
    }
    byDate.set(date, current);
  });

  if (byDate.size === 0) return [];

  const dataDates = [...byDate.keys()].sort();
  const minDataDate = dataDates[0];
  const maxDataDate = dataDates[dataDates.length - 1];

  // Si start es anterior al primer dato histórico (ej. 2020-01-01), arrancar desde minDataDate para no crear años vacíos
  const effectiveStart = start && start >= minDataDate ? start : minDataDate;
  // Extender hasta end si la ventana continúa después del último registro de gasto
  const effectiveEnd = end && end >= maxDataDate ? end : maxDataDate;

  const startDate = new Date(`${effectiveStart}T12:00:00Z`);
  const endDate = new Date(`${effectiveEnd}T12:00:00Z`);
  const validRange = !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime()) && startDate <= endDate;
  const orderedDates: string[] = [];

  if (validRange) {
    const cursor = new Date(startDate);
    while (cursor <= endDate && orderedDates.length < 400) {
      orderedDates.push(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
  } else {
    orderedDates.push(...dataDates);
  }

  return orderedDates.map((date) => {
    const value = byDate.get(date);
    return { date, spend_daily: value?.spend ?? 0, budget_daily: value ? value.budget : null };
  });
}

function formatTooltipDate(dateStr: unknown): string {
  if (!dateStr) return "";
  const s = String(dateStr).slice(0, 10);
  try {
    const parts = s.split("-");
    if (parts.length === 3) {
      const year = parts[0];
      const monthIdx = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
      return `${day} ${months[monthIdx] || parts[1]} ${year}`;
    }
  } catch {
    /* fallback */
  }
  return s;
}

function SpendBudgetChart({
  data,
  actions = [],
  clientSlug,
  onHitoCreated,
}: {
  data: DataRecord[];
  actions?: DataRecord[];
  clientSlug?: string;
  onHitoCreated?: () => void;
}) {
  const minWidth = Math.max(760, Math.min(data.length, 60) * 36 + 100);
  const { containerRef, width } = useMeasuredChartWidth(minWidth);
  const { session } = useDashboard();
  const canManageHitos = Boolean(session?.canManageHitos);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [pinnedIdx, setPinnedIdx] = useState<number | null>(null);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHitoModalOpen, setIsHitoModalOpen] = useState(false);
  const [editingHito, setEditingHito] = useState<ChartHitoItem | null>(null);

  const handleDeleteHito = async (h: ChartHitoItem) => {
    if (!h.id || !clientSlug) return;
    if (!window.confirm(`¿Seguro que deseas eliminar el hito "${h.title}"?`)) return;
    try {
      await deleteClientHito({ id_item: h.id, client_slug: clientSlug });
      setPinnedIdx(null);
      setHoveredIdx(null);
      setIsHoveringTooltip(false);
      onHitoCreated?.();
    } catch (err: any) {
      alert(err?.message || "No fue posible eliminar el hito");
    }
  };

  if (!data.length) return <p className="chart-empty">Sin datos diarios en la ventana.</p>;

  const height = 286;
  const margin = { top: 24, right: 20, bottom: 46, left: 74 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const rawMax = Math.max(1, ...data.flatMap((row) => [number(row.spend_daily), number(row.budget_daily)]));
  const yMax = rawMax;
  const ticks = Array.from({ length: 5 }, (_, index) => yMax * index / 4);
  const labelStep = Math.max(1, Math.ceil(data.length / 8));
  const step = plotWidth / Math.max(data.length, 1);
  const xAt = (index: number) => margin.left + step * (index + 0.5);
  const yAt = (value: number) => margin.top + plotHeight - value / yMax * plotHeight;
  const barWidth = Math.min(26, Math.max(8, step * 0.48));
  const budgetPoints = data
    .map((row, index) => row.budget_daily === null || row.budget_daily === undefined
      ? null
      : `${xAt(index)},${yAt(number(row.budget_daily))}`)
    .filter((point): point is string => point !== null)
    .join(" ");

  const executedActions = useMemo(() => mapActionsToDataPoints(actions, data), [actions, data]);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (pinnedIdx !== null || isHoveringTooltip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    if (mouseX < margin.left || mouseX > width - margin.right) {
      setHoveredIdx(null);
      return;
    }
    let closestIdx = 0;
    let minDist = Infinity;
    data.forEach((_, idx) => {
      const px = xAt(idx);
      const dist = Math.abs(px - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closestIdx = idx;
      }
    });
    setHoveredIdx(closestIdx);
    setMousePos({ x: xAt(closestIdx), y: Math.max(margin.top, Math.min(mouseY, height - margin.bottom)) });
  };

  const activeIdx = pinnedIdx !== null ? pinnedIdx : hoveredIdx;
  const activeRow = activeIdx !== null ? data[activeIdx] : null;
  const activeDate = activeRow ? String(activeRow.date || "").slice(0, 10) : "";
  const activeHitos = activeDate
    ? actions
        .filter((a) => a.executed_at && String(a.executed_at).slice(0, 10) === activeDate)
        .map((a) => ({
          id: a.id_item ? String(a.id_item) : (a.id ? String(a.id) : undefined),
          title: String(a.title || a.titulo || "Hito ejecutado"),
          date: String(a.executed_at).slice(0, 10),
          comment: a.comentario ? String(a.comentario) : undefined,
          category: a.category || a.dominio ? String(a.category || a.dominio) : undefined,
        }))
    : [];

  return (
    <div className="paid-series-wrap">
      <div className="paid-series-meta" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="paid-series-legend" aria-label="Leyenda del gráfico">
          <span><i className="is-spend" />Gasto</span>
          <span><i className="is-over" />Sobre budget</span>
          <span><i className="is-budget-line" />Meta diaria</span>
          {executedActions.length > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: "#38bdf8", fontWeight: 600 }}>
              <span>🚩</span> Hito ejecutado
            </span>
          )}
        </div>
        {clientSlug && canManageHitos && (
          <button
            type="button"
            className="button button-ghost button-small"
            onClick={() => {
              setEditingHito(null);
              setIsHitoModalOpen(true);
            }}
            style={{ fontSize: "11px", display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 8px" }}
          >
            <Flag size={12} color="#38bdf8" />
            <span>Registrar hito</span>
          </button>
        )}
      </div>
      <div
        className="time-chart-scroll"
        ref={containerRef}
        onMouseLeave={() => {
          if (pinnedIdx === null && !isHoveringTooltip) {
            setHoveredIdx(null);
          }
        }}
      >
        <svg
          className="time-chart paid-time-chart"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Gasto diario en barras y meta diaria en línea roja punteada"
          onMouseMove={handleMouseMove}
          onClick={() => {
            if (pinnedIdx !== null) {
              setPinnedIdx(null);
            } else if (hoveredIdx !== null) {
              setPinnedIdx(hoveredIdx);
            }
          }}
          style={{ cursor: "crosshair" }}
        >
          <defs>
            <linearGradient id="paid-spend-gradient" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#536bd0" /><stop offset="100%" stopColor="#7d96ff" />
            </linearGradient>
            <linearGradient id="paid-over-gradient" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#be3d48" /><stop offset="100%" stopColor="#ff636a" />
            </linearGradient>
          </defs>
          {ticks.map((tick) => {
            const y = yAt(tick);
            return <g key={tick}>
              <line className="chart-grid-line" x1={margin.left} x2={width - margin.right} y1={y} y2={y} />
              <text className="chart-axis-label" x={margin.left - 10} y={y} textAnchor="end" dominantBaseline="middle">{compactCurrency(tick)}</text>
            </g>;
          })}
          <text className="chart-axis-title" x={12} y={12}>CLP</text>

          {/* Línea de crosshair activa al hacer hover o pin */}
          {activeIdx !== null && (
            <line
              className="chart-crosshair-line"
              x1={xAt(activeIdx)}
              x2={xAt(activeIdx)}
              y1={margin.top}
              y2={margin.top + plotHeight}
            />
          )}

          {/* Banderas de Hitos */}
          {executedActions.map((action, i) => {
            if (action.idx === null || action.idx === undefined) return null;
            const x = xAt(action.idx);
            const actAny = action as any;
            return (
              <g
                key={actAny.id || actAny.id_item ? String(actAny.id || actAny.id_item) : i}
                className="chart-hito-flag"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setPinnedIdx(action.idx);
                  setHoveredIdx(action.idx);
                  setMousePos({ x, y: margin.top + 20 });
                }}
              >
                <line
                  x1={x}
                  x2={x}
                  y1={margin.top}
                  y2={margin.top + plotHeight}
                  stroke="#38bdf8"
                  strokeWidth="1.5"
                  strokeDasharray="4 3"
                />
                <circle cx={x} cy={margin.top - 7} r="10" fill="rgba(56, 189, 248, 0.18)" />
                <g transform={`translate(${x - 6}, ${margin.top - 15})`}>
                  <line x1="2" y1="2" x2="2" y2="16" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" />
                  <path d="M2 2 L12 5.5 L2 9 Z" fill="#38bdf8" stroke="#0284c7" strokeWidth="1" strokeLinejoin="round" />
                </g>
              </g>
            );
          })}

          {data.map((row, index) => {
            const spend = number(row.spend_daily);
            const budget = row.budget_daily === null || row.budget_daily === undefined ? null : number(row.budget_daily);
            const overBudget = budget !== null && budget > 0 && spend > budget;
            const x = xAt(index);
            const y = yAt(spend);
            const showLabel = data.length <= 12 || index === 0 || index === data.length - 1 || index % labelStep === 0;
            const isHovered = activeIdx === index;
            return <g key={text(row.date)}>
              <rect
                x={x - barWidth / 2}
                y={y}
                width={barWidth}
                height={Math.max(0, margin.top + plotHeight - y)}
                rx="4"
                fill={overBudget ? "url(#paid-over-gradient)" : "url(#paid-spend-gradient)"}
                opacity={activeIdx !== null && !isHovered ? 0.6 : 1}
                stroke={isHovered ? "#ffffff" : "none"}
                strokeWidth={isHovered ? 1.5 : 0}
              />
              {showLabel && <text className="chart-x-label" x={x} y={height - 17} textAnchor="middle">{shortDateLabel(row.date)}</text>}
            </g>;
          })}
          {budgetPoints && <polyline className="budget-target-line" points={budgetPoints} fill="none" />}
          {data.map((row, index) => {
            if (row.budget_daily === null || row.budget_daily === undefined) return null;
            const isHovered = activeIdx === index;
            return (
              <circle
                className="budget-target-point"
                key={`budget-${text(row.date)}`}
                cx={xAt(index)}
                cy={yAt(number(row.budget_daily))}
                r={isHovered ? 5 : 3}
                stroke={isHovered ? "#ffffff" : "#141822"}
                strokeWidth={isHovered ? 2 : 1.5}
              />
            );
          })}
        </svg>

        {/* Tooltip interactivo estilo Semrush */}
        {activeRow && (
          <ChartTooltip
            visible={activeIdx !== null}
            x={mousePos.x}
            y={mousePos.y}
            title={formatTooltipDate(activeRow.date)}
            items={[
              { color: "#536bd0", label: "Gasto", value: formatValue(activeRow.spend_daily, "currency") },
              {
                color: "#ff636a",
                label: "Meta diaria",
                value: activeRow.budget_daily !== null && activeRow.budget_daily !== undefined
                  ? formatValue(activeRow.budget_daily, "currency")
                  : "Sin meta",
              },
            ]}
            hitos={activeHitos}
            containerWidth={width}
            containerHeight={height}
            canManageHitos={canManageHitos}
            isPinned={pinnedIdx !== null}
            onClose={() => {
              setPinnedIdx(null);
              setHoveredIdx(null);
              setIsHoveringTooltip(false);
            }}
            onMouseEnter={() => setIsHoveringTooltip(true)}
            onMouseLeave={() => {
              setIsHoveringTooltip(false);
              if (pinnedIdx === null) setHoveredIdx(null);
            }}
            onEditHito={(h) => {
              setEditingHito(h);
              setIsHitoModalOpen(true);
            }}
            onDeleteHito={handleDeleteHito}
          />
        )}
      </div>

      {clientSlug && (
        <RegisterHitoModal
          isOpen={isHitoModalOpen}
          onClose={() => {
            setIsHitoModalOpen(false);
            setEditingHito(null);
          }}
          clientSlug={clientSlug}
          initialDomain="Paid"
          initialHito={editingHito}
          onHitoCreated={onHitoCreated}
        />
      )}
    </div>
  );
}

function aggregateSeoTrend(data: DataRecord[]): DataRecord[] {
  const byDate = new Map<string, { clicks: number; impressions: number }>();
  data.forEach((row) => {
    const date = text(row.date, "").slice(0, 10);
    if (!date) return;
    const current = byDate.get(date) ?? { clicks: 0, impressions: 0 };
    current.clicks += number(row.clics);
    current.impressions += number(row.impresiones);
    byDate.set(date, current);
  });
  return [...byDate.entries()].sort(([dateA], [dateB]) => dateA.localeCompare(dateB)).map(([date, value]) => ({
    date,
    clics: value.clicks,
    impresiones: value.impressions,
  }));
}

function SeoTrendChart({
  data,
  actions = [],
  clientSlug,
  onHitoCreated,
}: {
  data: DataRecord[];
  actions?: DataRecord[];
  clientSlug?: string;
  onHitoCreated?: () => void;
}) {
  let cleanData = [...data];
  while (cleanData.length > 0 && number(cleanData[cleanData.length - 1].clics) === 0 && number(cleanData[cleanData.length - 1].impresiones) === 0) {
    cleanData.pop();
  }
  
  const minWidth = Math.max(780, Math.min(cleanData.length, 90) * 24 + 120);
  const { containerRef, width } = useMeasuredChartWidth(minWidth);
  const { session } = useDashboard();
  const canManageHitos = Boolean(session?.canManageHitos);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [pinnedIdx, setPinnedIdx] = useState<number | null>(null);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHitoModalOpen, setIsHitoModalOpen] = useState(false);
  const [editingHito, setEditingHito] = useState<ChartHitoItem | null>(null);

  const handleDeleteHito = async (h: ChartHitoItem) => {
    if (!h.id || !clientSlug) return;
    if (!window.confirm(`¿Seguro que deseas eliminar el hito "${h.title}"?`)) return;
    try {
      await deleteClientHito({ id_item: h.id, client_slug: clientSlug });
      setPinnedIdx(null);
      setHoveredIdx(null);
      setIsHoveringTooltip(false);
      onHitoCreated?.();
    } catch (err: any) {
      alert(err?.message || "No fue posible eliminar el hito");
    }
  };

  if (!cleanData.length) return <p className="chart-empty">Sin datos orgánicos en la ventana.</p>;

  const height = 300;
  const margin = { top: 26, right: 76, bottom: 48, left: 70 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const clicksMax = niceAxisMax(Math.max(1, ...cleanData.map((row) => number(row.clics))) * 1.08);
  const impressionsMax = niceAxisMax(Math.max(1, ...cleanData.map((row) => number(row.impresiones))) * 1.08);
  const ticks = Array.from({ length: 5 }, (_, index) => index / 4);
  const labelStep = Math.max(1, Math.ceil(cleanData.length / 8));

  const xAt = (index: number) => cleanData.length === 1
    ? margin.left + plotWidth / 2
    : margin.left + plotWidth * index / (cleanData.length - 1);
    
  const yClicks = (value: number) => margin.top + plotHeight - value / clicksMax * plotHeight;
  const yImpressions = (value: number) => margin.top + plotHeight - value / impressionsMax * plotHeight;
  const clicksPath = cleanData.map((row, index) => `${index === 0 ? "M" : "L"} ${xAt(index)} ${yClicks(number(row.clics))}`).join(" ");
  const impressionsPath = cleanData.map((row, index) => `${index === 0 ? "M" : "L"} ${xAt(index)} ${yImpressions(number(row.impresiones))}`).join(" ");

  const executedActions = useMemo(() => mapActionsToDataPoints(actions, cleanData), [actions, cleanData]);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (pinnedIdx !== null || isHoveringTooltip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    if (mouseX < margin.left || mouseX > width - margin.right) {
      setHoveredIdx(null);
      return;
    }
    let closestIdx = 0;
    let minDist = Infinity;
    cleanData.forEach((_, idx) => {
      const px = xAt(idx);
      const dist = Math.abs(px - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closestIdx = idx;
      }
    });
    setHoveredIdx(closestIdx);
    setMousePos({ x: xAt(closestIdx), y: Math.max(margin.top, Math.min(mouseY, height - margin.bottom)) });
  };

  const activeIdx = pinnedIdx !== null ? pinnedIdx : hoveredIdx;
  const activeRow = activeIdx !== null ? cleanData[activeIdx] : null;
  const activeDate = activeRow ? String(activeRow.date || "").slice(0, 10) : "";
  const activeHitos = activeDate
    ? actions
        .filter((a) => a.executed_at && String(a.executed_at).slice(0, 10) === activeDate)
        .map((a) => ({
          id: a.id_item ? String(a.id_item) : (a.id ? String(a.id) : undefined),
          title: String(a.title || a.titulo || "Hito ejecutado"),
          date: String(a.executed_at).slice(0, 10),
          comment: a.comentario ? String(a.comentario) : undefined,
          category: a.category || a.dominio ? String(a.category || a.dominio) : undefined,
        }))
    : [];

  return (
    <div className="seo-chart-wrap">
      <div className="seo-chart-legend" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }} aria-label="Leyenda del gráfico SEO">
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <span><i className="is-clicks" />Clics · eje izquierdo</span>
          <span><i className="is-impressions" />Impresiones · eje derecho</span>
          {executedActions.length > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: "#38bdf8", fontWeight: 600 }}>
              <span>🚩</span> Hito ejecutado
            </span>
          )}
        </div>
        {clientSlug && canManageHitos && (
          <button
            type="button"
            className="button button-ghost button-small"
            onClick={() => {
              setEditingHito(null);
              setIsHitoModalOpen(true);
            }}
            style={{ fontSize: "11px", display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 8px" }}
          >
            <Flag size={12} color="#38bdf8" />
            <span>Registrar hito</span>
          </button>
        )}
      </div>
      <div
        className="time-chart-scroll"
        ref={containerRef}
        onMouseLeave={() => {
          if (pinnedIdx === null && !isHoveringTooltip) {
            setHoveredIdx(null);
          }
        }}
      >
        <svg
          className="time-chart seo-time-chart"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Evolución de clics e impresiones en líneas"
          onMouseMove={handleMouseMove}
          onClick={() => {
            if (pinnedIdx !== null) {
              setPinnedIdx(null);
            } else if (hoveredIdx !== null) {
              setPinnedIdx(hoveredIdx);
            }
          }}
          style={{ cursor: "crosshair" }}
        >
          {ticks.map((ratio) => {
            const y = margin.top + plotHeight - ratio * plotHeight;
            return <g key={ratio}>
              <line className="chart-grid-line" x1={margin.left} x2={width - margin.right} y1={y} y2={y} />
              <text className="chart-axis-label is-clicks" x={margin.left - 10} y={y} textAnchor="end" dominantBaseline="middle">{compactNumber(clicksMax * ratio)}</text>
              <text className="chart-axis-label is-impressions" x={width - margin.right + 10} y={y} textAnchor="start" dominantBaseline="middle">{compactNumber(impressionsMax * ratio)}</text>
            </g>;
          })}
          <text className="chart-axis-title is-clicks" x={12} y={13}>CLICS</text>
          <text className="chart-axis-title is-impressions" x={width - 12} y={13} textAnchor="end">IMPRESIONES</text>
          
          {/* Línea de crosshair activa al hacer hover o pin */}
          {activeIdx !== null && (
            <line
              className="chart-crosshair-line"
              x1={xAt(activeIdx)}
              x2={xAt(activeIdx)}
              y1={margin.top}
              y2={margin.top + plotHeight}
            />
          )}

          {/* Banderas de Hitos mapeadas exactamente por fecha de dato */}
          {executedActions.map((action, i) => {
            if (action.idx === null || action.idx === undefined) return null;
            const x = xAt(action.idx);
            const actAny = action as any;
            return (
              <g
                key={actAny.id || actAny.id_item ? String(actAny.id || actAny.id_item) : i}
                className="chart-hito-flag"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setPinnedIdx(action.idx);
                  setHoveredIdx(action.idx);
                  setMousePos({ x, y: margin.top + 20 });
                }}
              >
                <line 
                  x1={x} x2={x} 
                  y1={margin.top} y2={margin.top + plotHeight} 
                  stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="4 3" 
                />
                <circle cx={x} cy={margin.top - 7} r="10" fill="rgba(56, 189, 248, 0.18)" />
                <g transform={`translate(${x - 6}, ${margin.top - 15})`}>
                  <line x1="2" y1="2" x2="2" y2="16" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" />
                  <path d="M2 2 L12 5.5 L2 9 Z" fill="#38bdf8" stroke="#0284c7" strokeWidth="1" strokeLinejoin="round" />
                </g>
              </g>
            );
          })}

          <path className="seo-line is-clicks" d={clicksPath} fill="none" opacity={activeIdx !== null ? 0.85 : 1} />
          <path className="seo-line is-impressions" d={impressionsPath} fill="none" opacity={activeIdx !== null ? 0.85 : 1} />
          
          {cleanData.map((row, index) => {
            const x = xAt(index);
            const showLabel = cleanData.length <= 12 || index === 0 || index === cleanData.length - 1 || index % labelStep === 0;
            return <g key={text(row.date)}>
              <circle className="seo-point is-clicks" cx={x} cy={yClicks(number(row.clics))} r="3" />
              <circle className="seo-point is-impressions" cx={x} cy={yImpressions(number(row.impresiones))} r="3" />
              {showLabel && <text className="chart-x-label" x={x} y={height - 17} textAnchor="middle">{shortDateLabel(row.date)}</text>}
            </g>;
          })}

          {/* Highlight circles on hover/pin */}
          {activeIdx !== null && (
            <>
              <circle
                className="chart-active-point"
                cx={xAt(activeIdx)}
                cy={yClicks(number(cleanData[activeIdx].clics))}
                r={5.5}
                fill="#3b82f6"
              />
              <circle
                className="chart-active-point"
                cx={xAt(activeIdx)}
                cy={yImpressions(number(cleanData[activeIdx].impresiones))}
                r={5.5}
                fill="#8b5cf6"
              />
            </>
          )}
        </svg>

        {/* Tooltip interactivo estilo Semrush */}
        {activeRow && (
          <ChartTooltip
            visible={activeIdx !== null}
            x={mousePos.x}
            y={mousePos.y}
            title={formatTooltipDate(activeRow.date)}
            items={[
              { color: "#3b82f6", label: "Clics", value: formatValue(activeRow.clics, "integer") },
              { color: "#8b5cf6", label: "Impresiones", value: formatValue(activeRow.impresiones, "integer") },
            ]}
            hitos={activeHitos}
            containerWidth={width}
            containerHeight={height}
            canManageHitos={canManageHitos}
            isPinned={pinnedIdx !== null}
            onClose={() => {
              setPinnedIdx(null);
              setHoveredIdx(null);
              setIsHoveringTooltip(false);
            }}
            onMouseEnter={() => setIsHoveringTooltip(true)}
            onMouseLeave={() => {
              setIsHoveringTooltip(false);
              if (pinnedIdx === null) setHoveredIdx(null);
            }}
            onEditHito={(h) => {
              setEditingHito(h);
              setIsHitoModalOpen(true);
            }}
            onDeleteHito={handleDeleteHito}
          />
        )}
      </div>

      {clientSlug && (
        <RegisterHitoModal
          isOpen={isHitoModalOpen}
          onClose={() => {
            setIsHitoModalOpen(false);
            setEditingHito(null);
          }}
          clientSlug={clientSlug}
          initialDomain="SEO"
          initialHito={editingHito}
          onHitoCreated={onHitoCreated}
        />
      )}
    </div>
  );
}

function SubTabs<T extends string>({ items, active, onChange }: { items: Array<{ id: T; label: string }>; active: T; onChange: (value: T) => void }) {
  return (
    <div className="detail-subtabs" role="tablist">
      {items.map((item) => (
        <button type="button" role="tab" aria-selected={active === item.id} className={active === item.id ? "is-active" : ""} key={item.id} onClick={() => onChange(item.id)}>
          {item.label}
        </button>
      ))}
    </div>
  );
}

function SectionPanel({
  title,
  eyebrow,
  copy,
  count,
  isUpdating: explicitUpdating,
  children,
}: {
  title: string;
  eyebrow: string;
  copy?: string;
  count?: number;
  isUpdating?: boolean;
  children: ReactNode;
}) {
  const contextUpdating = useUpdating();
  const isUpdating = explicitUpdating ?? contextUpdating;
  return (
    <section className={`panel detail-panel ${isUpdating ? "is-updating" : ""}`} style={{ position: "relative" }}>
      <div className="panel-heading">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
          {copy && <p className="panel-copy">{copy}</p>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isUpdating && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "3px 10px",
                borderRadius: "9999px",
                fontSize: "12px",
                fontWeight: 500,
                color: "var(--accent, #38bdf8)",
                background: "rgba(56, 189, 248, 0.1)",
                border: "1px solid rgba(56, 189, 248, 0.25)",
              }}
            >
              <Loader2 className="animate-spin" size={12} />
              Actualizando…
            </span>
          )}
          {count !== undefined && <span className="count-badge">{count}</span>}
        </div>
      </div>
      <div style={{ position: "relative", minHeight: isUpdating ? "120px" : undefined }}>
        {isUpdating && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(15, 23, 42, 0.45)",
              backdropFilter: "blur(1.5px)",
              borderRadius: "6px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                background: "rgba(15, 23, 42, 0.85)",
                border: "1px solid rgba(56, 189, 248, 0.3)",
                borderRadius: "20px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                color: "#e2e8f0",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              <Loader2 className="animate-spin text-cyan-400" size={16} />
              <span>Consultando BigQuery…</span>
            </div>
          </div>
        )}
        <div style={{ opacity: isUpdating ? 0.35 : 1, transition: "opacity 0.2s ease", pointerEvents: isUpdating ? "none" : "auto" }}>
          {children}
        </div>
      </div>
    </section>
  );
}

function DeviceSplit({ devices, lcp }: { devices: DataRecord[]; lcp?: unknown }) {
  const standard = ["mobile", "desktop", "tablet", "smart tv"];
  const aggregated = new Map<string, { device: string; sessions: number; clicks: number; engaged: number }>();
  devices.forEach((row) => {
    const device = text(row.device, "desconocido").trim().toLowerCase() || "desconocido";
    const sessions = number(row.sesiones);
    const current = aggregated.get(device) ?? { device, sessions: 0, clicks: 0, engaged: 0 };
    current.sessions += sessions;
    current.clicks += number(row.clics);
    current.engaged += row.sesiones_engaged == null ? sessions * number(row.engagement_rate) : number(row.sesiones_engaged);
    aggregated.set(device, current);
  });
  standard.forEach((device) => {
    if (!aggregated.has(device)) aggregated.set(device, { device, sessions: 0, clicks: 0, engaged: 0 });
  });
  const order = new Map([...standard, "desconocido"].map((device, index) => [device, index]));
  const rows = [...aggregated.values()]
    .filter((row) => row.device !== "desconocido" || row.sessions > 0)
    .sort((left, right) => (order.get(left.device) ?? 99) - (order.get(right.device) ?? 99) || right.sessions - left.sessions);
  const totalSessions = rows.reduce((sum, row) => sum + row.sessions, 0);
  const mobile = rows.find((row) => row.device === "mobile");
  const mobileShare = mobile && totalSessions ? mobile.sessions / totalSessions : 0;
  const lcpSeconds = lcp == null ? null : number(lcp);

  const icon = (device: string) => {
    if (device === "mobile") return <Smartphone size={15} />;
    if (device === "desktop") return <Monitor size={15} />;
    if (device === "tablet") return <Tablet size={15} />;
    if (device === "smart tv") return <Tv size={15} />;
    return <Globe2 size={15} />;
  };

  return <SectionPanel eyebrow="Experiencia" title="Distribución por dispositivo" count={devices.length}>
    <div className="device-split-list">
      {rows.map((row) => {
        const share = totalSessions ? row.sessions / totalSessions : 0;
        const engagement = row.sessions ? row.engaged / row.sessions : null;
        return <article className={`device-split-card is-${row.device.replaceAll(" ", "-")}`} key={row.device}>
          <div className="device-split-head">
            <span>{icon(row.device)} {row.device}</span>
            <strong>{Math.round(share * 100)}%</strong>
          </div>
          <div className="device-split-track"><i style={{ width: `${Math.round(share * 100)}%` }} /></div>
          <p>{formatValue(row.sessions, "integer")} ses · {formatValue(row.clicks, "integer")} clics</p>
          <small className={engagement != null && engagement < .3 ? "is-danger" : "is-good"}>engagement {engagement == null ? "—" : formatValue(engagement, "percent")}</small>
        </article>;
      })}
      {!devices.length && <p className="chart-empty">Sin datos de dispositivos para esta URL.</p>}
    </div>
    {mobileShare >= .6 && lcpSeconds != null && lcpSeconds >= 2.5 && <div className="device-lcp-warning">
      <Info size={16} />
      <span><strong>{Math.round(mobileShare * 100)}% mobile + LCP {lcpSeconds.toFixed(1)}s crítico.</strong> El problema de velocidad afecta a la mayoría de las visitas de esta URL.</span>
    </div>}
  </SectionPanel>;
}

function formatAuditDate(value: unknown): string {
  if (!value) return "sin fecha registrada";
  const raw = String(value);
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = new Date(dateOnly ? `${raw}T12:00:00Z` : raw);
  if (Number.isNaN(date.getTime())) return text(value);
  return new Intl.DateTimeFormat("es-CL", {
    dateStyle: "medium",
    ...(dateOnly ? {} : { timeStyle: "short" as const }),
    timeZone: dateOnly ? "UTC" : "America/Santiago",
  }).format(date);
}

interface FeedbackTarget {
  clientSlug: string;
  domain: "SEO" | "MKT";
  insight: string;
  level?: string;
  element?: string;
}

/** Selección explícita de 👍/👎 + comentario + botón Enviar.
 *  El envío nunca es implícito: el pulgar solo marca la intención y "Enviar"
 *  es el único disparo hacia `/api/gertrudis/feedback`. */
function AuditFeedback({ target, title, placeholder }: { target: FeedbackTarget; title: string; placeholder?: string }) {
  const [rating, setRating] = useState<"POSITIVO" | "NEGATIVO" | null>(null);
  const [comment, setComment] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  const missingComment = rating === "NEGATIVO" && !comment.trim();
  const blocked = !rating || missingComment || state === "sending";
  const hint = state === "sent" ? message
    : state === "error" ? message
    : !rating ? "Marca 👍 o 👎 para habilitar el envío."
    : missingComment ? "Con 👎 el comentario es obligatorio: sin el porqué el feedback no entrena nada."
    : "Listo para enviar.";

  const send = () => {
    if (blocked || !rating) return;
    setState("sending");
    setMessage(null);
    void submitGertrudisFeedback({
      clientSlug: target.clientSlug,
      insight: target.insight,
      rating,
      comment: comment.trim(),
      domain: target.domain,
      auditType: target.domain === "SEO" ? "SEO_Radiografia_URL" : "Paid_Media_Suggestor",
      level: target.level,
      element: target.element,
    })
      .then(() => {
        setState("sent");
        setMessage(`Comentario enviado (${rating === "POSITIVO" ? "👍 útil" : "👎 corregir"}). Queda pendiente de revisión del Tech Lead.`);
        setRating(null);
        setComment("");
      })
      .catch((cause) => {
        setState("error");
        setMessage(cause instanceof Error ? cause.message : "No fue posible guardar el comentario.");
      });
  };

  return <div className={`audit-feedback${state === "sent" ? " is-sent" : ""}`}>
    <div className="audit-feedback-head">
      <span>{title}</span>
      <div className="audit-feedback-rating" role="group" aria-label="Calificación de la auditoría">
        <button type="button" aria-pressed={rating === "POSITIVO"} className={rating === "POSITIVO" ? "is-active is-positive" : ""} onClick={() => { setRating("POSITIVO"); setState("idle"); }}>
          <ThumbsUp size={15} /> Útil
        </button>
        <button type="button" aria-pressed={rating === "NEGATIVO"} className={rating === "NEGATIVO" ? "is-active is-negative" : ""} onClick={() => { setRating("NEGATIVO"); setState("idle"); }}>
          <ThumbsDown size={15} /> Corregir
        </button>
      </div>
    </div>
    <textarea
      value={comment}
      onChange={(event) => { setComment(event.target.value); if (state !== "idle") setState("idle"); }}
      placeholder={placeholder ?? (rating === "NEGATIVO" ? "Obligatorio: ¿qué está mal o qué falta en esta auditoría?" : "Comentario opcional para el equipo")}
    />
    <div className="audit-feedback-footer">
      <button className="button button-primary audit-feedback-send" type="button" disabled={blocked} onClick={send}>
        {state === "sending" ? <><span className="button-spinner" /> Enviando…</> : <><Send size={15} /> Enviar comentario</>}
      </button>
      <small className={state === "error" ? "is-error" : state === "sent" ? "is-ok" : ""}>{hint}</small>
    </div>
  </div>;
}

function renderSimpleMarkdown(text: string): string {
  if (!text) return "";
  let html = text.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  html = html.replace(/\n/g, "<br />");
  return html;
}

function AuditBody({ audit, target, actionsTitle }: { audit: GertrudisAuditPayload; target: Omit<FeedbackTarget, "insight">; actionsTitle: string }) {
  const insight = (audit.insight ?? "").trim();
  let actionBlocks = audit.actions?.length ? audit.actions : [];
  
  if (!actionBlocks.length && insight) {
    const chunks = insight.split(/(?=(?:🔴|🟡|🟢|⚪|⚫|🔵)\s*\*?Diagnóstico:?\*?)/i).map(c => c.trim()).filter(Boolean);
    actionBlocks = chunks.length > 1 ? chunks : [insight];
  }

  const splitActions = actionBlocks.length > 1;
  return <>
    {!splitActions && <div className="gertrudis-response-copy" dangerouslySetInnerHTML={{ __html: renderSimpleMarkdown(insight) }} />}
    {!splitActions && <AuditFeedback
      target={{ ...target, insight }}
      title="Comentario sobre esta auditoría"
      placeholder="¿Qué tan útil fue esta auditoría?"
    />}
    {splitActions && <div className="feedback-list">
      <h4 className="feedback-list-title">{actionsTitle}</h4>
      {actionBlocks.map((action, index) => {
        const actionText = typeof action === "string" ? action : (action && typeof action === "object" && "title" in action ? `**${String(action.title)}**${action.priority ? ` (${String(action.priority)})` : ""}: ${String(action.impact ?? "")}` : JSON.stringify(action));
        return (
          <div className="feedback-item" key={`${index}-${actionText.slice(0, 24)}`}>
            <div dangerouslySetInnerHTML={{ __html: renderSimpleMarkdown(actionText) }}></div>
            <AuditFeedback target={{ ...target, insight: actionText }} title={`¿Te sirve la acción ${index + 1}?`} />
          </div>
        );
      })}
    </div>}
  </>;
}

function GertrudisCard({ audit, busy, error, onRun, label, clientSlug, domain, cardRef }: {
  audit?: GertrudisAuditPayload;
  busy: boolean;
  error?: string | null;
  onRun: () => void;
  label: string;
  clientSlug: string;
  domain: "SEO" | "MKT";
  cardRef?: React.Ref<HTMLDivElement>;
}) {
  const insight = audit?.insight?.trim();
  const history = audit?.history ?? [];
  const previous = history.slice(1);
  // Las auditorías persistidas en `sys_ai_feedback` traen `id`; las recién
  // generadas todavía no, y así distinguimos caché de corrida nueva.
  const fresh = Boolean(insight) && !audit?.id;
  const target = { clientSlug, domain, level: audit?.level, element: audit?.element };
  return <div className="gertrudis-card-anchor" ref={cardRef}>
    <SectionPanel
      eyebrow="Auditoría inteligente"
      title="Gertrudis · plan de acción"
      copy={insight
        ? "La última auditoría se recupera desde el datalake al abrir esta vista; generar una nueva no borra las anteriores."
        : "Todavía no existe una auditoría persistida para este elemento."}
    >
      <div className="gertrudis-actions">
        <button className="button button-primary gertrudis-run" type="button" disabled={busy} onClick={onRun}>
          {busy ? <><span className="button-spinner" /> Gertrudis está analizando…</> : <><BrainCircuit size={16} /> {insight ? "Generar nueva auditoría" : label}</>}
        </button>
        {insight
          ? <span className={`audit-stamp${fresh ? " is-fresh" : ""}`}>
              <Clock size={14} />
              <span>Auditoría del <strong>{formatAuditDate(audit?.timestamp)}</strong></span>
              <i>{fresh ? "recién generada" : "cargada desde el historial"}</i>
            </span>
          : <small>La respuesta queda guardada y aparecerá inmediatamente en el historial.</small>}
      </div>
      {insight && <div className="gertrudis-response">
        <div className="gertrudis-response-title"><BrainCircuit size={17} /><span>Recomendaciones y acciones</span></div>
        <AuditBody audit={audit as GertrudisAuditPayload} target={target} actionsTitle="Acciones priorizadas" />
      </div>}
      {previous.length > 0 && <details className="audit-history-disclosure">
        <summary>Ver auditorías anteriores <span>{previous.length}</span></summary>
        <div className="audit-history-list">
          {previous.map((item, index) => <details key={item.id ?? `${item.timestamp}-${index}`}>
            <summary><span>Auditoría {previous.length - index}</span><time>{formatAuditDate(item.timestamp)}</time></summary>
            <AuditBody
              audit={item}
              target={{ clientSlug, domain, level: item.level ?? audit?.level, element: item.element ?? audit?.element }}
              actionsTitle="Acciones de esa corrida"
            />
          </details>)}
        </div>
      </details>}
      {error && <div className="gertrudis-error">{error}</div>}
    </SectionPanel>
  </div>;
}

function ActionExecuteButton({ action, clientSlug, dominio }: { action: DataRecord; clientSlug: string; dominio: string }) {
  const [executing, setExecuting] = useState(false);
  const [executedAt, setExecutedAt] = useState<string | null>((action.executed_at as string) || null);

  if (executedAt) {
    return <span style={{ color: "#4ade80", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
      <CheckCircle size={14} /> Ejecutada
    </span>;
  }

  return <button 
    className="button button-small" 
    style={{ fontSize: "11px", padding: "4px 8px" }}
    disabled={executing}
    onClick={async () => {
      const comentario = window.prompt("Agrega un comentario sobre la ejecución de la acción (opcional):");
      if (comentario === null) return; // cancelado por el usuario
      
      setExecuting(true);
      try {
        const response = await fetch("/api/audit/action/execute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_item: action.id, client_slug: clientSlug, dominio, comentario })
        });
        if (response.ok) {
          setExecutedAt(new Date().toISOString());
        }
      } catch (err) {
        console.error(err);
      } finally {
        setExecuting(false);
      }
    }}
  >
    {executing ? "..." : "Marcar Ejecutada"}
  </button>;
}

function SeoAuditOverview({
  clientSlug,
  findings,
  actions,
  snapshot,
  history = [],
  usedData,
}: {
  clientSlug: string;
  findings: DataRecord[];
  actions: DataRecord[];
  snapshot?: string | null;
  history?: SeoAuditSnapshot[];
  usedData?: GertrudisUsedData;
}) {
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [localFindings, setLocalFindings] = useState(findings);
  const [localActions, setLocalActions] = useState(actions);
  const [localSnapshot, setLocalSnapshot] = useState(snapshot);
  const [localHistory, setLocalHistory] = useState(history);
  const [localUsedData, setLocalUsedData] = useState(usedData);
  const [showUsedData, setShowUsedData] = useState(false);

  useEffect(() => {
    setLocalFindings(findings);
    setLocalActions(actions);
    setLocalSnapshot(snapshot);
    setLocalHistory(history);
    setLocalUsedData(usedData);
  }, [clientSlug, findings, actions, snapshot, history, usedData]);

  const snapshots = localHistory.length ? localHistory : [{ snapshot: localSnapshot, findings: localFindings, actions: localActions, usedData: localUsedData }];
  const [selectedSnapshot, setSelectedSnapshot] = useState(() => text(snapshots[0]?.snapshot, "actual"));
  useEffect(() => setSelectedSnapshot(text(snapshots[0]?.snapshot, "actual")), [clientSlug, snapshots[0]?.snapshot]);

  const selected = snapshots.find((item) => text(item.snapshot, "actual") === selectedSnapshot) ?? snapshots[0];
  const visibleFindings = selected?.findings ?? localFindings;
  const visibleActions = selected?.actions ?? localActions;
  const activeUsedData = selected?.usedData ?? localUsedData;

  const handleGenerate = () => {
    setGenerating(true);
    setError(null);
    setSuccessMsg(null);
    triggerGertrudisSeoAudit(clientSlug)
      .then((res) => {
        setLocalFindings(res.findings);
        setLocalActions(res.actions);
        setLocalSnapshot(res.snapshot);
        setLocalHistory(res.auditHistory);
        if (res.usedData) {
          setLocalUsedData(res.usedData);
        }
        setSelectedSnapshot(text(res.snapshot, "actual"));
        setShowUsedData(true);
        setSuccessMsg("¡Auditoría generada y materializada en BigQuery con éxito!");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "No fue posible generar la auditoría con Gertrudis."))
      .finally(() => setGenerating(false));
  };

  const handleDownload = () => {
    setDownloading(true);
    setError(null);
    downloadSeoAudit(clientSlug)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "No fue posible generar el entregable HTML."))
      .finally(() => setDownloading(false));
  };

  const kpis = activeUsedData?.kpis ?? {};

  return <SectionPanel
    eyebrow="Gertrudis · Auditoría SEO"
    title="Auditoría SEO integral"
    copy={selected?.snapshot ? `Snapshot ${formatAuditDate(selected.snapshot)}` : "Sin snapshot materializado"}
    count={visibleFindings.length}
  >
    {snapshots.length > 1 && <div className="audit-snapshot-picker"><label>Historial de auditorías<select value={selectedSnapshot} onChange={(event) => setSelectedSnapshot(event.target.value)}>{snapshots.map((item) => { const value = text(item.snapshot, "actual"); return <option value={value} key={value}>{formatAuditDate(item.snapshot)}</option>; })}</select></label><small>{snapshots.length} snapshots conservados</small></div>}
    
    {!visibleFindings.length && !visibleActions.length && <div className="detail-notice"><Info size={17} /><span>Este cliente todavía no tiene hallazgos ni acciones materializados. Pulsa el botón inferior para generarlos con Gertrudis.</span></div>}
    
    {!!visibleFindings.length && <div className="audit-findings-grid">
      {visibleFindings.map((finding, index) => <article className={`audit-finding is-${text(finding.sentido, "riesgo")}`} key={text(finding.id, String(index))}>
        <div className="audit-finding-head"><span>{text(finding.icon, "•")}</span><strong>{text(finding.title)}</strong><i>{text(finding.priority)}</i></div>
        <p>{text(finding.desc)}</p>
        <small>{text(finding.tag)}</small>
      </article>)}
    </div>}
    
    {!!visibleActions.length && <div className="audit-action-block">
      <h3>Acciones priorizadas</h3>
      <DataTable data={visibleActions} columns={[
        { key: "id", label: "ID" }, { key: "title", label: "Acción" }, { key: "category", label: "Categoría" },
        { key: "priority", label: "Prioridad" }, { key: "impact", label: "Impacto esperado" },
        { key: "executed", label: "Estado", render: (row) => <ActionExecuteButton action={row} clientSlug={clientSlug} dominio="SEO" /> }
      ]} />
    </div>}

    {error && <div className="gertrudis-error">{error}</div>}
    {successMsg && <div className="detail-notice" style={{ borderColor: "#22c55e", color: "#86efac" }}><CheckCircle size={16} /><span>{successMsg}</span></div>}

    {/* Contenedor colapsable: Datos que usó Gertrudis */}
    <div className="audit-used-data-section">
      <button
        type="button"
        className="button button-ghost audit-used-data-toggle"
        onClick={() => setShowUsedData(!showUsedData)}
      >
        <Sparkles size={14} />
        {showUsedData ? "Ocultar datos que usó Gertrudis" : "📊 Ver datos que usó Gertrudis"}
      </button>

      {showUsedData && (
        <div className="audit-used-data-card">
          <div className="audit-used-data-header">
            <h4>Insumos consolidados consumidos por Gertrudis</h4>
            <span className="audit-used-data-badge">GSC + Semrush + GA4</span>
          </div>

          <div className="audit-used-kpis-grid">
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Clics GSC (90d)</span>
              <strong>{kpis.clics != null ? formatValue(kpis.clics, "integer") : "—"}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Impresiones GSC</span>
              <strong>{kpis.impresiones != null ? formatValue(kpis.impresiones, "integer") : "—"}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">CTR prom.</span>
              <strong>{kpis.ctr != null ? `${Number(kpis.ctr).toFixed(2)}%` : "—"}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Posición prom.</span>
              <strong>{kpis.posicionPromedio != null ? Number(kpis.posicionPromedio).toFixed(1) : "—"}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Keywords Semrush</span>
              <strong>{kpis.srTotalKW != null ? formatValue(kpis.srTotalKW, "integer") : "—"}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Top 3 / Top 10</span>
              <strong>{kpis.srTop3 != null && kpis.srTop10 != null ? `${kpis.srTop3} / ${kpis.srTop10}` : "—"}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Leads GA4 (tot / org)</span>
              <strong>{kpis.totalLeads != null ? `${kpis.totalLeads} / ${kpis.leadsOrg ?? 0}` : "—"}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Errores Site Audit</span>
              <strong>{kpis.auditIssues != null ? formatValue(kpis.auditIssues, "integer") : "0"}</strong>
            </div>
          </div>

          {((activeUsedData?.topKeywords?.length ?? 0) > 0 || (activeUsedData?.gapKeywords?.length ?? 0) > 0 || (activeUsedData?.competitors?.length ?? 0) > 0) && (
            <div className="audit-used-chips-container">
              {!!activeUsedData?.topKeywords?.length && (
                <div className="audit-used-chips-group">
                  <span className="chips-group-title">Keywords propias destacadas:</span>
                  <div className="chips-list">
                    {activeUsedData.topKeywords.map((kw, i) => (
                      <span className="audit-chip chip-own" key={i}>{kw}</span>
                    ))}
                  </div>
                </div>
              )}
              {!!activeUsedData?.gapKeywords?.length && (
                <div className="audit-used-chips-group">
                  <span className="chips-group-title">Keywords de oportunidad (Gap competidores):</span>
                  <div className="chips-list">
                    {activeUsedData.gapKeywords.map((kw, i) => (
                      <span className="audit-chip chip-gap" key={i}>{kw}</span>
                    ))}
                  </div>
                </div>
              )}
              {!!activeUsedData?.competitors?.length && (
                <div className="audit-used-chips-group">
                  <span className="chips-group-title">Competidores directos comparados:</span>
                  <div className="chips-list">
                    {activeUsedData.competitors.map((comp, i) => (
                      <span className="audit-chip chip-comp" key={i}>{comp}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {Boolean(
            (activeUsedData?.hitos && activeUsedData.hitos.length > 0) ||
            actions.some((a) => a.executed)
          ) && (
            <div className="audit-used-chips-container" style={{ marginTop: "14px" }}>
              <div className="audit-used-chips-group">
                <span className="chips-group-title" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>🚩</span> Hitos de gestión / contexto histórico considerados:
                </span>
                <div className="chips-list" style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "6px" }}>
                  {(activeUsedData?.hitos && activeUsedData.hitos.length > 0
                    ? activeUsedData.hitos
                    : actions.filter((a) => a.executed).map((a) => ({
                        date: String(a.executed_at || "").slice(0, 10),
                        title: a.title,
                        comment: a.comentario,
                      }))
                  ).map((h: any, i: number) => (
                    <div key={i} style={{ display: "flex", alignItems: "baseline", gap: "8px", fontSize: "12px", background: "rgba(56, 189, 248, 0.08)", padding: "4px 10px", borderRadius: "6px", border: "1px solid rgba(56, 189, 248, 0.2)" }}>
                      <strong style={{ color: "#38bdf8", fontFamily: "var(--font-mono, monospace)" }}>{h.date || "Hito"}</strong>
                      <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{h.title}</span>
                      {h.comment && <span style={{ color: "#94a3b8" }}>— {h.comment}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>

    <div className="gertrudis-actions audit-export-actions">
      <button className="button button-primary" type="button" disabled={generating} onClick={handleGenerate}>
        <BrainCircuit size={16} />
        {generating ? <><span className="button-spinner" /> Gertrudis está analizando datos de GSC y Semrush…</> : "Generar auditoría con Gertrudis"}
      </button>
      <button className="button button-secondary" type="button" disabled={downloading} onClick={handleDownload}>
        <Download size={15} />
        {downloading ? "Generando descargable…" : "Descargar entregable HTML"}
      </button>
      <small>Cruza Search Console, Semrush y GA4. Los hallazgos y acciones se materializan de inmediato en BigQuery y se reflejan en pantalla.</small>
    </div>
  </SectionPanel>;
}

/** Caché de radiografías por (cliente, URL, ventana) que sobrevive al desmontaje
 *  del tab: volver a una URL ya consultada no re-dispara BQ ni pierde la
 *  auditoría recién generada. Vive en el módulo, no en estado de React, porque
 *  `UrlRadiography` se desmonta al cambiar de tab primario. */
const urlDetailCache = new Map<string, UrlDetailPayload>();
const URL_DETAIL_CACHE_MAX = 60;

function urlDetailKey(clientSlug: string, url: string, window: string): string {
  return `${clientSlug}|${window}|${url}`;
}

function cacheUrlDetail(key: string, payload: UrlDetailPayload): UrlDetailPayload {
  if (urlDetailCache.size >= URL_DETAIL_CACHE_MAX) {
    const oldest = urlDetailCache.keys().next().value;
    if (oldest !== undefined) urlDetailCache.delete(oldest);
  }
  urlDetailCache.set(key, payload);
  return payload;
}

function clearUrlDetailCache(): void {
  urlDetailCache.clear();
}

function UrlRadiography({
  clientSlug,
  window,
  urls,
  initialUrl,
  actions = [],
  onHitoCreated,
}: {
  clientSlug: string;
  window: string;
  urls: string[];
  initialUrl?: string;
  actions?: DataRecord[];
  onHitoCreated?: () => void;
}) {
  const [selectedUrl, setSelectedUrl] = useState(() => pickDefaultUrl(urls, initialUrl));
  const [detail, setDetail] = useState<UrlDetailPayload | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keywordFilter, setKeywordFilter] = useState<"Todas" | "Marca" | "Descubrimiento">("Todas");
  const [pageSpeedBusy, setPageSpeedBusy] = useState(false);
  const [pageSpeedMessage, setPageSpeedMessage] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [showUsedData, setShowUsedData] = useState(false);
  const bypassCacheRef = useRef(false);
  const auditCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const preferred = pickDefaultUrl(urls, initialUrl);
    if (!urls.includes(selectedUrl) || (initialUrl && normalizedUrlKey(selectedUrl) !== normalizedUrlKey(initialUrl))) setSelectedUrl(preferred);
  }, [urls, selectedUrl, initialUrl]);

  useEffect(() => {
    if (!selectedUrl) return;
    const key = urlDetailKey(clientSlug, selectedUrl, window);
    const bypass = bypassCacheRef.current;
    bypassCacheRef.current = false;
    const cached = bypass ? undefined : urlDetailCache.get(key);
    if (cached) {
      setDetail(cached);
      setFromCache(true);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchUrlDetail(clientSlug, selectedUrl, window)
      .then((payload) => {
        cacheUrlDetail(key, payload);
        if (!cancelled) { setDetail(payload); setFromCache(false); }
      })
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : "No fue posible cargar la radiografía."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientSlug, selectedUrl, window, reloadToken]);

  if (!urls.length) return <div className="detail-notice"><Info size={17} /><span>No hay URLs con datos en esta ventana para construir una radiografía.</span></div>;

  const radiography = record(detail?.radiography);
  const kpis = record(detail?.kpis);
  const trend = aggregateSeoTrend(detail?.evolution ?? []);
  const latestAudit = detail?.latestAudit;
  const effectiveActions = (actions && actions.length > 0) ? actions : ((detail?.actions || []) as DataRecord[]);
  const reload = () => {
    bypassCacheRef.current = true;
    setReloadToken((value) => value + 1);
  };
  const runAudit = () => {
    setRunning(true);
    setError(null);
    const key = urlDetailKey(clientSlug, selectedUrl, window);
    void runGertrudisSeo(clientSlug, selectedUrl, window)
      .then((audit) => {
        setShowUsedData(true);
        setDetail((current) => {
          const previous = current?.latestAudit;
          const previousLatest = previous?.insight ? { ...previous, history: undefined } : null;
          const history = [audit, ...(previous?.history ?? (previousLatest ? [previousLatest] : []))];
          const next = { ...(current ?? {}), latestAudit: { ...audit, history } };
          cacheUrlDetail(key, next);
          return next;
        });
        requestAnimationFrame(() => auditCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "No fue posible invocar a Gertrudis."))
      .finally(() => setRunning(false));
  };
  const refreshPageSpeed = () => {
    setPageSpeedBusy(true);
    setPageSpeedMessage(null);
    void runPageSpeed(clientSlug, selectedUrl)
      .then((result) => setPageSpeedMessage(result.message))
      .catch((cause) => setPageSpeedMessage(cause instanceof Error ? cause.message : "No fue posible iniciar PageSpeed."))
      .finally(() => setPageSpeedBusy(false));
  };
  const filteredKeywords = (detail?.keywords ?? []).filter((row) => {
    if (keywordFilter === "Todas") return true;
    const kind = text(row.tipo_busqueda).toUpperCase();
    return keywordFilter === "Marca" ? kind.includes("MARCA") || kind.includes("BRAND") : !(kind.includes("MARCA") || kind.includes("BRAND"));
  });
  const urlCannibalization = (detail?.cannibalization ?? []).filter((row) => text(row.url_dominante) === selectedUrl || text(row.url_canibalizada) === selectedUrl);

  return <div className="detail-stack">
    <div className="radiography-toolbar">
      <div><span className="eyebrow">Radiografía URL</span><h2>Diagnóstico por landing</h2></div>
      <div className="radiography-toolbar-actions">
        <label><Search size={15} /><select value={selectedUrl} onChange={(event) => setSelectedUrl(event.target.value)}>{urls.map((url) => <option value={url} key={url}>{url}</option>)}</select></label>
        <button className="button button-ghost" type="button" disabled={loading || running} onClick={reload} title="Vuelve a consultar BigQuery ignorando la caché de sesión"><RefreshCw size={15} />Actualizar</button>
        <button className="button button-primary" type="button" disabled={running || loading} onClick={runAudit}><BrainCircuit size={16} />{running ? "Analizando…" : "Auditar con Gertrudis"}</button>
      </div>
    </div>
    {fromCache && !loading && <p className="cache-note"><Clock size={13} /> Datos y auditoría servidos desde la caché de esta sesión. Usa <strong>Actualizar</strong> para releer BigQuery.</p>}
    {loading && <div className="detail-loading compact"><span /><p>Cargando radiografía…</p></div>}
    {error && <div className="gertrudis-error">{error}</div>}
    {!loading && detail && <>
      <UrlContextBar url={selectedUrl} kpis={kpis} radiography={radiography} latestAudit={latestAudit} />
      
      <div className="detail-metric-grid">
        <DetailMetric label="Sesiones 30d" value={formatValue(kpis.sesiones_totales_30d, "integer")} />
        <DetailMetric label="Engagement" value={formatValue(kpis.engagement_rate_avg, "percent")} tone={number(kpis.engagement_rate_avg) < .3 ? "danger" : "good"} />
        <DetailMetric label="CTR orgánico" value={formatValue(kpis.ctr_total_avg, "percent")} />
        <DetailMetric label="Posición prom." value={formatValue(kpis.posicion_promedio, "decimal")} />
        <DetailMetric label="LCP" value={radiography.lcp_seconds == null ? "—" : `${formatValue(radiography.lcp_seconds, "decimal")}s`} tone={number(radiography.lcp_seconds) >= 2.5 ? "danger" : "good"} />
        <DetailMetric label="CLS" value={formatValue(radiography.cls_score, "decimal")} />
        <DetailMetric label="Performance" value={formatValue(radiography.performance_score, "integer")} tone={number(radiography.performance_score) < 50 ? "danger" : number(radiography.performance_score) < 90 ? "warning" : "good"} />
        <DetailMetric label="GEO ready" value={radiography.geo_ready_flag ? "SÍ" : "NO"} tone={radiography.geo_ready_flag ? "good" : "danger"} />
      </div>

      <div className="technical-actions">
        <button className="button button-ghost" type="button" disabled={pageSpeedBusy} onClick={refreshPageSpeed}>{pageSpeedBusy ? "Iniciando…" : "Auditar PageSpeed ahora"}</button>
        {pageSpeedMessage && <small>{pageSpeedMessage}</small>}
      </div>

      {radiography.intencion_rota_flag && (
        <div className="detail-notice is-danger">
          <Info size={17} />
          <span>Diagnóstico determinístico: esta landing recibe demanda orgánica, pero su engagement no acompaña la intención de búsqueda. Revisa promesa, contenido above-the-fold y CTA.</span>
        </div>
      )}

      <div className="radiography-primary-grid">
        <SectionPanel eyebrow="Search Console" title="Evolución de la URL" copy={`${text(detail.window?.period_start)} → ${text(detail.window?.period_end)}`}>
          <SeoTrendChart data={trend} actions={effectiveActions} clientSlug={clientSlug} onHitoCreated={onHitoCreated} />
        </SectionPanel>
        <DeviceSplit devices={detail.devices ?? []} lcp={radiography.lcp_seconds} />
      </div>

      <GertrudisCard audit={latestAudit} busy={running} error={null} onRun={runAudit} label="Generar auditoría de esta URL" clientSlug={clientSlug} domain="SEO" cardRef={auditCardRef} />

      {/* Contenedor colapsable: Datos que usó Gertrudis (Landing específica) */}
      <div className="audit-used-data-section">
        <button
          type="button"
          className="button button-ghost audit-used-data-toggle"
          onClick={() => setShowUsedData(!showUsedData)}
        >
          <Sparkles size={14} />
          {showUsedData ? "Ocultar datos que usó Gertrudis" : "📊 Ver datos que usó Gertrudis"}
        </button>

        {showUsedData && (
          <div className="audit-used-data-card">
            <div className="audit-used-data-header">
              <h4>Insumos consolidados consumidos por Gertrudis (Landing específica)</h4>
              <span className="audit-used-data-badge">GSC + GA4 + PageSpeed</span>
            </div>

            <div className="audit-used-kpis-grid">
              <div className="used-kpi-pill">
                <span className="used-kpi-label">Sesiones (30d)</span>
                <strong>{kpis.sesiones_totales_30d != null ? formatValue(kpis.sesiones_totales_30d, "integer") : "—"}</strong>
              </div>
              <div className="used-kpi-pill">
                <span className="used-kpi-label">Engagement rate</span>
                <strong>{kpis.engagement_rate_avg != null ? formatValue(kpis.engagement_rate_avg, "percent") : "—"}</strong>
              </div>
              <div className="used-kpi-pill">
                <span className="used-kpi-label">CTR orgánico</span>
                <strong>{kpis.ctr_total_avg != null ? formatValue(kpis.ctr_total_avg, "percent") : "—"}</strong>
              </div>
              <div className="used-kpi-pill">
                <span className="used-kpi-label">Posición prom.</span>
                <strong>{kpis.posicion_promedio != null ? formatValue(kpis.posicion_promedio, "decimal") : "—"}</strong>
              </div>
              <div className="used-kpi-pill">
                <span className="used-kpi-label">LCP</span>
                <strong>{radiography.lcp_seconds != null ? `${formatValue(radiography.lcp_seconds, "decimal")}s` : "—"}</strong>
              </div>
              <div className="used-kpi-pill">
                <span className="used-kpi-label">CLS</span>
                <strong>{radiography.cls_score != null ? formatValue(radiography.cls_score, "decimal") : "—"}</strong>
              </div>
              <div className="used-kpi-pill">
                <span className="used-kpi-label">Performance Core</span>
                <strong>{radiography.performance_score != null ? formatValue(radiography.performance_score, "integer") : "—"}</strong>
              </div>
              <div className="used-kpi-pill">
                <span className="used-kpi-label">GEO Ready</span>
                <strong>{radiography.geo_ready_flag ? "SÍ" : "NO"}</strong>
              </div>
            </div>

            {!!detail?.keywords?.length && (
              <div className="audit-used-chips-container">
                <div className="audit-used-chips-group">
                  <span className="chips-group-title">Top consultas Search Console que apuntan a esta URL:</span>
                  <div className="audit-used-chips-list">
                    {detail.keywords.slice(0, 10).map((kw: any, idx: number) => (
                      <span className="used-chip" key={idx}>
                        {text(kw.palabra_clave)} (pos. {formatValue(kw.posicion_promedio, "decimal")})
                      </span>
                    ))}
                    {detail.keywords.length > 10 && (
                      <span className="used-chip-more">+{detail.keywords.length - 10} más</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {Boolean(effectiveActions.some((a) => a.executed_at)) && (
              <div className="audit-used-chips-container" style={{ marginTop: "14px" }}>
                <div className="audit-used-chips-group">
                  <span className="chips-group-title" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>🚩</span> Hitos de gestión del cliente considerados:
                  </span>
                  <div className="chips-list" style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "6px" }}>
                    {effectiveActions.filter((a) => a.executed_at).map((h: any, i: number) => (
                      <div key={i} style={{ display: "flex", alignItems: "baseline", gap: "8px", fontSize: "12px", background: "rgba(56, 189, 248, 0.08)", padding: "4px 10px", borderRadius: "6px", border: "1px solid rgba(56, 189, 248, 0.2)" }}>
                        <strong style={{ color: "#38bdf8", fontFamily: "var(--font-mono, monospace)" }}>{String(h.executed_at).slice(0, 10)}</strong>
                        <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{h.title || h.titulo}</span>
                        {h.comentario && <span style={{ color: "#94a3b8" }}>— {h.comentario}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <SectionPanel eyebrow="Consultas" title="Top keywords de la URL" count={filteredKeywords.length}>
        <div className="platform-filter">
          <button type="button" className={keywordFilter === "Todas" ? "is-active" : ""} onClick={() => setKeywordFilter("Todas")}>Todas</button>
          <button type="button" className={keywordFilter === "Marca" ? "is-active" : ""} onClick={() => setKeywordFilter("Marca")}>Marca</button>
          <button type="button" className={keywordFilter === "Descubrimiento" ? "is-active" : ""} onClick={() => setKeywordFilter("Descubrimiento")}>Descubrimiento</button>
        </div>
        <DataTable data={filteredKeywords} columns={[
          { key: "palabra_clave", label: "Keyword" }, { key: "tipo_busqueda", label: "Intención" }, { key: "impresiones", label: "Impresiones", format: "integer" },
          { key: "clics", label: "Clics", format: "integer" }, { key: "ctr", label: "CTR", format: "percent" }, { key: "posicion", label: "Pos.", format: "decimal" },
        ]} />
      </SectionPanel>

      <section className="url-analysis-tools" aria-label="Herramientas de análisis de la URL">
        <div className="url-analysis-tools-heading">
          <span className="eyebrow">Profundizar</span>
          <h3>Herramientas de esta URL</h3>
          <p>Se abren sólo cuando necesitas investigar; la salud y Gertrudis permanecen visibles arriba.</p>
        </div>
        <details className="panel disclosure-panel">
          <summary>Laboratorio Wolf <span>{detail.laboratory?.length ?? 0}</span></summary>
          <DataTable data={detail.laboratory ?? []} columns={[{ key: "id_experimento", label: "Experimento" }, { key: "plataforma", label: "Plataforma" }, { key: "regla_testeada", label: "Regla" }, { key: "estado", label: "Estado" }, { key: "dias_transcurridos", label: "Días medidos", format: "integer" }, { key: "dias_restantes", label: "Restantes", format: "integer" }, { key: "delta_ctr_pct", label: "Δ CTR", format: "percent" }, { key: "delta_cpa_pct", label: "Δ CPA", format: "percent" }, { key: "delta_engagement_pct", label: "Δ Engagement", format: "percent" }, { key: "calificacion_gertrudis", label: "Veredicto" }]} />
        </details>
        <details className="panel disclosure-panel">
          <summary>Canibalización de la URL <span>{urlCannibalization.length}</span></summary>
          <DataTable data={urlCannibalization} columns={[{ key: "palabra_clave", label: "Keyword" }, { key: "tipo_canibalizacion", label: "Tipo" }, { key: "url_dominante", label: "Dominante" }, { key: "url_canibalizada", label: "Canibalizada" }, { key: "impresiones_totales", label: "Impresiones", format: "integer" }]} />
        </details>
        <details className="panel disclosure-panel">
          <summary>Preparación GEO <span>{detail.geo?.length ?? 0}</span></summary>
          <div className="detail-notice"><Info size={17} /><span>{radiography.geo_ready_flag ? "Esta URL está preparada para GEO." : text(radiography.funnel_type) === "BoF" ? "URL transaccional sin preparación GEO: implementa Product Schema y una respuesta directa." : "Implementa Article/FAQ Schema y respuestas citables para mejorar elegibilidad GEO."}</span></div>
          <DataTable data={detail.geo ?? []} columns={[{ key: "url_clean", label: "URL" }, { key: "Clics", label: "Clics", format: "integer" }, { key: "Schema_Detectado", label: "Schema" }]} />
        </details>
      </section>

      <details className="panel disclosure-panel">
        <summary>Pipeline SEO on-demand</summary>
        <div className="command-list">
          {Object.entries(detail.commands ?? {}).map(([key, value]) => <div key={key}><strong>{key.replaceAll("_", " ")}</strong><code>{value}</code></div>)}
        </div>
      </details>
    </>}
  </div>;
}

function SeoDetail({
  data,
  clientSlug,
  initialUrl,
  tab,
  onTabChange,
  onHitoCreated,
  isUpdating: explicitUpdating,
}: {
  data: SeoDetailPayload;
  clientSlug: string;
  initialUrl?: string;
  tab: SeoTab;
  onTabChange: (tab: SeoTab) => void;
  onHitoCreated?: () => void;
  isUpdating?: boolean;
}) {
  const contextUpdating = useUpdating();
  const isUpdating = explicitUpdating ?? contextUpdating;
  const audit = record(data.audit);
  const kpis = record(audit.kpis);
  const keywords = rows(audit.srKeywords);
  const competitors = data.semrushCompetitors ?? rows(audit.srCompetitors);
  const conversion = record(audit.conversion);
  const channels = rows(conversion.channels);
  const topPages = rows(audit.topConvertingPages);
  const urlOptions = useMemo(() => {
    const values = new Set<string>();
    (data.urls ?? []).forEach((row) => { const value = text(row.url_clean, ""); if (value) values.add(value); });
    [data.strikingDistance, data.brokenIntent, data.lcp, data.lowCtr, data.decliningTraffic, data.geo].forEach((list) => {
      (list ?? []).forEach((row) => { const value = text(row.url_clean, ""); if (value) values.add(value); });
    });
    keywords.forEach((row) => { const value = text(row.url, ""); if (value) values.add(value.replace(/^https?:\/\//, "")); });
    return [...values].sort((left, right) => left.localeCompare(right));
  }, [data.urls, data.strikingDistance, data.brokenIntent, data.lcp, data.lowCtr, data.decliningTraffic, data.geo, keywords]);
  const [exploreData, setExploreData] = useState<SemrushExplorePayload | null>(null);
  const [exploreLoading, setExploreLoading] = useState(false);

  useEffect(() => {
    if (tab === "explore" && !exploreData && !exploreLoading) {
      setExploreLoading(true);
      fetchClientDetail(clientSlug, "semrush-explore", text(data.window?.window, "MTD"))
        .then((payload) => setExploreData(payload as SemrushExplorePayload))
        .catch(() => console.error("Error cargando semrush-explore"))
        .finally(() => setExploreLoading(false));
    }
  }, [tab, exploreData, exploreLoading, clientSlug, data.window?.window]);

  const seoTrend = useMemo(() => aggregateSeoTrend(data.trend ?? []), [data.trend]);
  const totalClicks = seoTrend.length ? seoTrend.reduce((sum, row) => sum + number(row.clics), 0) : number(kpis.clics);
  const totalImpressions = seoTrend.length ? seoTrend.reduce((sum, row) => sum + number(row.impresiones), 0) : number(kpis.impresiones);

  return (
    <div className="detail-stack">
      {tab === "overview" && <>
        <div className="detail-metric-grid">
          <DetailMetric label="Impresiones" value={formatValue(totalImpressions, "integer")} />
          <DetailMetric label="Clics orgánicos" value={formatValue(totalClicks, "integer")} note={text(data.window?.window)} />
          <DetailMetric label="CTR" value={totalImpressions ? `${(totalClicks / totalImpressions * 100).toFixed(2)}%` : formatValue(kpis.ctr, "percent")} />
          <DetailMetric label="Posición promedio" value={formatValue(kpis.posicionPromedio, "decimal")} />
        </div>
        {/* Módulo Ejecutivo Semrush: Salud Técnica, Rastreo, AI Search y Bots (Estilo Semrush) */}
        <SemrushSiteAuditCards
          siteHealthTrend={data.siteHealthTrend ?? []}
          siteAuditIssues={data.siteAuditIssues ?? []}
          siteAuditPages={data.siteAuditPages ?? []}
          isUpdating={isUpdating}
        />

        <SectionPanel eyebrow="Search Console" title="Evolución orgánica" copy={`${text(data.window?.period_start)} → ${text(data.window?.period_end)}`}>
          <SeoTrendChart data={seoTrend} actions={(data.actions || []) as DataRecord[]} clientSlug={clientSlug} onHitoCreated={onHitoCreated} />
        </SectionPanel>

        {/* Share de Visibilidad con Leyenda Didáctica */}
        <SectionPanel eyebrow="Semrush" title="Share de visibilidad" copy="Distribución de keywords propias por rango de posición en la SERP, por snapshot de auditoría">
          <VisibilidadShareChart data={data.visibilidadShare ?? []} />
        </SectionPanel>

        {/* Semrush Hub: Keywords Orgánicas y Competidores Orgánicos unificados */}
        <div className="detail-two-columns wide-first">
          <SectionPanel eyebrow="Semrush" title="Keywords orgánicas" count={keywords.length}>
            <DataTable data={keywords} columns={[
              { key: "kw", label: "Keyword", render: (r) => text(r.kw ?? r.palabra_clave, "—") },
              { key: "pos", label: "Pos.", render: (r) => formatValue(r.position ?? r.pos, "decimal") },
              { key: "volume", label: "Volumen", render: (r) => formatValue(r.volume ?? r.search_volume, "integer") },
              { key: "traffic", label: "Tráfico", render: (r) => formatValue(r.traffic, "integer") },
              { key: "url", label: "URL", render: (r) => text(r.url ?? r.url_clean, "—") },
            ]} />
          </SectionPanel>
          <SectionPanel eyebrow="Semrush" title="Competidores orgánicos" count={competitors.length}>
            <DataTable data={competitors} columns={[
              { key: "domain", label: "Dominio", render: (r) => text(r.competitor_domain ?? r.domain, "—") },
              { key: "relevance", label: "Relevancia", render: (r) => formatValue(r.relevance, "percent") },
              { key: "commonKw", label: "KW comunes", render: (r) => formatValue(r.common_keywords ?? r.commonKw, "integer") },
              { key: "orgKw", label: "KW orgánicas", render: (r) => formatValue(r.organic_keywords ?? r.orgKw, "integer") },
              { key: "orgTraffic", label: "Tráfico org.", render: (r) => formatValue(r.organic_traffic ?? r.orgTraffic, "integer") },
            ]} />
          </SectionPanel>
        </div>

        {/* GEO AI Engine Tracking del Google Sheet */}
        <GeoTrackingPanel geoTracking={data.geoTracking} clientSlug={clientSlug} />

        <div className="section-divider-heading"><span className="eyebrow">URL destacada</span><h2>Radiografía de la HOME</h2><p>La HOME se selecciona por defecto; puedes cambiarla por cualquier URL del cliente sin salir del resumen.</p></div>
        <UrlRadiography
          clientSlug={clientSlug}
          window={text(data.window?.window, "MTD")}
          urls={urlOptions}
          initialUrl={initialUrl}
          actions={(data.actions || []) as DataRecord[]}
          onHitoCreated={onHitoCreated}
        />
      </>}

      {tab === "opportunities" && <>
        <SeoAuditOverview clientSlug={clientSlug} findings={data.findings ?? []} actions={data.actions ?? []} snapshot={data.findingsSnapshot} history={data.auditHistory} usedData={data.usedData} />
        <SectionPanel eyebrow="Semrush" title="Salud técnica, evolutivo" copy="Health Score (Site Audit) e Issues por tipo -- dos fuentes con calendarios distintos, ver nota">
          <div className="detail-two-columns">
            <SmallLineChart data={data.siteHealthTrend ?? []} valueKey="health_score" label="Health Score" yDomainMax={100} color="#22c55e" />
            <SmallBarChart data={data.siteHealthTrend ?? []} format="integer" series={[
              { key: "errors_failed_checks", label: "Errores", color: "#f56565" },
              { key: "warnings_failed_checks", label: "Advertencias", color: "#f6ad55" },
            ]} />
          </div>
          <p className="chart-note">Health Score viene de la ingesta automática (API); Errores/Advertencias del export manual de Issues -- pueden no compartir fecha.</p>
        </SectionPanel>
        <div className="detail-two-columns">
          <SectionPanel eyebrow="Auditoría Semrush" title="Problemas técnicos (Issues)" count={data.siteAuditIssues?.length ?? 0}>
            <DataTable data={data.siteAuditIssues ?? []} columns={[
              { key: "issue_type", label: "Gravedad" }, { key: "issue_name", label: "Problema" }, { key: "issues_count", label: "Cant.", format: "integer" }
            ]} empty="Sin problemas técnicos detectados o no hay datos." />
          </SectionPanel>
          <SectionPanel eyebrow="Auditoría Semrush" title="Páginas con errores" count={data.siteAuditPages?.length ?? 0}>
            <DataTable data={data.siteAuditPages ?? []} columns={[
              { key: "page_url", label: "URL" }, { key: "issues_count", label: "Errores", format: "integer" }
            ]} empty="Sin páginas con errores o no hay datos." />
          </SectionPanel>
        </div>
        <SiteAuditPageIssuesPanel
          pageIssues={data.siteAuditPageIssues ?? []}
          crawledPages={data.siteAuditPages ?? []}
          clientSlug={clientSlug}
        />
        <div className="detail-two-columns">
          <SectionPanel eyebrow="Oportunidad" title="Striking distance" count={data.strikingDistance?.length ?? 0}>
            <DataTable data={data.strikingDistance ?? []} columns={[
              { key: "palabra_clave", label: "Keyword" }, { key: "posicion", label: "Pos.", format: "decimal" },
              { key: "impresiones", label: "Impresiones", format: "integer" }, { key: "url_clean", label: "URL" },
            ]} />
          </SectionPanel>
          <SectionPanel eyebrow="Calidad" title="Intención rota y LCP" count={(data.brokenIntent?.length ?? 0) + (data.lcp?.length ?? 0)}>
            <DataTable data={[...(data.brokenIntent ?? []), ...(data.lcp ?? [])]} columns={[
              { key: "url_clean", label: "URL" }, { key: "clics_seo", label: "Clics", format: "integer" },
              { key: "engagement_rate_real", label: "Engagement", format: "percent" }, { key: "lcp_seconds", label: "LCP", format: "decimal" },
            ]} />
          </SectionPanel>
        </div>
        <div className="detail-two-columns">
          <SectionPanel eyebrow="SERP" title="Alta impresión, bajo CTR" count={data.lowCtr?.length ?? 0}>
            <DataTable data={data.lowCtr ?? []} columns={[
              { key: "url_clean", label: "URL" }, { key: "impresiones", label: "Impresiones", format: "integer" },
              { key: "clics", label: "Clics", format: "integer" }, { key: "ctr", label: "CTR", format: "percent" },
            ]} />
          </SectionPanel>
          <SectionPanel eyebrow="Experiencia" title="Distribución por dispositivo" count={data.devices?.length ?? 0}>
            <DataTable data={data.devices ?? []} columns={[
              { key: "device", label: "Dispositivo" }, { key: "sesiones", label: "Sesiones", format: "integer" },
              { key: "engagement_rate", label: "Engagement", format: "percent" },
            ]} />
          </SectionPanel>
        </div>
        <div className="detail-two-columns">
          <SectionPanel eyebrow="Tendencia" title="Tráfico decreciente" count={data.decliningTraffic?.length ?? 0}><DataTable data={data.decliningTraffic ?? []} columns={[{ key: "url_clean", label: "URL" }, { key: "sesiones_actual", label: "Sesiones actuales", format: "integer" }, { key: "sesiones_anterior", label: "Periodo anterior", format: "integer" }, { key: "variacion_pct", label: "Variación", format: "percent" }]} /></SectionPanel>
          <SectionPanel eyebrow="Rankings" title="Movimientos de posición" count={data.movements?.length ?? 0}>
            <DataTable data={data.movements ?? []} columns={[
              { key: "url_clean", label: "URL" },
              { key: "posicion_actual", label: "Actual", format: "decimal" },
              { key: "posicion_anterior", label: "Anterior", format: "decimal" },
              { key: "delta_posicion", label: "Cambio", format: "decimal" },
              { key: "impresiones", label: "Impresiones", format: "integer" }
            ]} />
          </SectionPanel>
        </div>
        <SectionPanel eyebrow="GEO" title="URLs sin Schema" count={data.geo?.length ?? 0}><DataTable data={data.geo ?? []} columns={[{ key: "url_clean", label: "URL" }, { key: "Clics", label: "Clics", format: "integer" }, { key: "Schema_Detectado", label: "Schema" }]} /></SectionPanel>
      </>}

      {tab === "explore" && <div className="detail-two-columns wide-first">
        <SectionPanel eyebrow="Semrush" title="Keywords orgánicas" count={keywords.length}>
          <DataTable data={keywords} columns={[
            { key: "kw", label: "Keyword", render: (r) => text(r.kw ?? r.palabra_clave, "—") },
            { key: "pos", label: "Pos.", render: (r) => formatValue(r.position ?? r.pos, "decimal") },
            { key: "volume", label: "Volumen", render: (r) => formatValue(r.volume ?? r.search_volume, "integer") },
            { key: "traffic", label: "Tráfico", render: (r) => formatValue(r.traffic, "integer") },
            { key: "url", label: "URL", render: (r) => text(r.url ?? r.url_clean, "—") },
          ]} />
        </SectionPanel>
        <SectionPanel eyebrow="SEO técnico" title="Canibalización" count={data.cannibalization?.length ?? 0}>
          <DataTable data={data.cannibalization ?? []} columns={[
            { key: "palabra_clave", label: "Keyword" }, { key: "tipo_canibalizacion", label: "Tipo" },
            { key: "impresiones_totales", label: "Impresiones", format: "integer" }, { key: "url_dominante", label: "URL dominante" },
          ]} />
        </SectionPanel>
      </div>}

      {tab === "explore" && <SectionPanel eyebrow="Semrush" title="Competidores orgánicos" count={competitors.length}>
        <DataTable data={competitors} columns={[
          { key: "domain", label: "Dominio", render: (r) => text(r.competitor_domain ?? r.domain, "—") },
          { key: "relevance", label: "Relevancia", render: (r) => formatValue(r.relevance, "percent") },
          { key: "commonKw", label: "KW comunes", render: (r) => formatValue(r.common_keywords ?? r.commonKw, "integer") },
          { key: "orgKw", label: "KW orgánicas", render: (r) => formatValue(r.organic_keywords ?? r.orgKw, "integer") },
          { key: "orgTraffic", label: "Tráfico org.", render: (r) => formatValue(r.organic_traffic ?? r.orgTraffic, "integer") },
        ]} />
      </SectionPanel>}

      {tab === "explore" && <>
        <div className="detail-metric-grid compact-grid">
          <DetailMetric label="Leads totales" value={formatValue(conversion.totalLeads ?? kpis.totalLeads, "integer")} />
          <DetailMetric label="Canales medidos" value={String(channels.length)} />
          <DetailMetric label="Páginas con conversión" value={String(topPages.length)} />
        </div>
        <div className="detail-two-columns">
          <SectionPanel eyebrow="GA4" title="Conversión por canal" count={channels.length}>
            <DataTable data={channels} columns={[{ key: "canal", label: "Canal" }, { key: "leads", label: "Leads", format: "integer" }]} />
          </SectionPanel>
          <SectionPanel eyebrow="Landing pages" title="Páginas que convierten" count={topPages.length}>
            <DataTable data={topPages} columns={[
              { key: "pagina", label: "Página", render: (r) => text(r.page ?? r.pagina ?? r.url, "—") },
              { key: "sesiones", label: "Sesiones", format: "integer" },
              { key: "leads", label: "Leads", format: "integer" },
              { key: "cr", label: "CTR", format: "percent" },
            ]} />
          </SectionPanel>
        </div>
      </>}

      {tab === "explore" && <>
        <BacklinksKpis data={{ audit }} />
        <div className="detail-two-columns">
          <Authority data={{ audit }} />
          <BacklinksDistribution data={{ audit }} />
        </div>
        <div className="detail-two-columns">
          <BacklinksTopDomains data={{ audit }} />
          <BacklinksTopAnchors data={{ audit }} />
        </div>
      </>}
      {tab === "explore" && <SectionPanel eyebrow="Semrush" title="Autoridad y backlinks, evolutivo" copy="Authority Score y dominios de referencia -- requiere que el cliente esté en la ingesta API de backlinks">
        {exploreLoading && !exploreData ? <div className="detail-loading compact"><span /></div> : (
          <div className="detail-two-columns">
            <SmallLineChart data={exploreData?.authorityTrend ?? []} valueKey="ascore" label="Authority Score" yDomainMax={100} color="#6f8cff" />
            <SmallLineChart data={exploreData?.authorityTrend ?? []} valueKey="domains_num" label="Dominios de referencia" color="#ad7bff" />
          </div>
        )}
      </SectionPanel>}
      {tab === "explore" && <div className="detail-two-columns wide-first">
        <SectionPanel eyebrow="Semrush" title="Keyword Gap" count={exploreData?.keywordGap?.length ?? 0}>
          {exploreLoading && !exploreData ? <div className="detail-loading compact"><span /></div> : (
            <DataTable data={exploreData?.keywordGap ?? []} columns={[
              { key: "keyword", label: "Keyword" }, { key: "search_volume", label: "Volumen", format: "integer" },
              { key: "keyword_difficulty", label: "KD", format: "integer" }, { key: "competitor_domain", label: "Competidor" }, { key: "competitor_position", label: "Posición", format: "integer" }
            ]} />
          )}
        </SectionPanel>
      </div>}
      {tab === "explore" && <SectionPanel eyebrow="Semrush" title="Cierre de brecha, evolutivo" copy="Volumen de búsqueda que el competidor captura y el cliente no, por dominio comparado">
        {exploreLoading && !exploreData ? <div className="detail-loading compact"><span /></div> : (
          <KeywordGapTrendChart data={exploreData?.keywordGapTrend ?? []} />
        )}
      </SectionPanel>}
      {tab === "explore" && <SectionPanel eyebrow="Semrush" title="Backlinks Atómicos (Últimos 500)" count={exploreData?.backlinksAtomic?.length ?? 0}>
          {exploreLoading && !exploreData ? <div className="detail-loading compact"><span /></div> : (
            <DataTable data={exploreData?.backlinksAtomic ?? []} columns={[
              { key: "source_title", label: "Fuente" }, { key: "source_url", label: "URL Fuente" },
              { key: "target_url_clean", label: "Destino" }, { key: "anchor", label: "Anchor" }, { key: "page_ascore", label: "AS", format: "integer" }
            ]} />
          )}
      </SectionPanel>}
    </div>
  );
}

function CpcClicksChart({
  data,
  actions = [],
  clientSlug,
  onHitoCreated,
}: {
  data: DataRecord[];
  actions?: DataRecord[];
  clientSlug?: string;
  onHitoCreated?: () => void;
}) {
  let cleanData = [...data];
  // Eliminar ceros planos iniciales para que el gráfico no tenga un mes de flatline muerto
  while (cleanData.length > 1 && number(cleanData[0].clics) === 0 && number(cleanData[0].conversions) === 0) {
    cleanData.shift();
  }

  const minWidth = Math.max(680, Math.min(cleanData.length, 90) * 24 + 120);
  const { containerRef, width } = useMeasuredChartWidth(minWidth);
  const { session } = useDashboard();
  const canManageHitos = Boolean(session?.canManageHitos);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [pinnedIdx, setPinnedIdx] = useState<number | null>(null);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHitoModalOpen, setIsHitoModalOpen] = useState(false);
  const [editingHito, setEditingHito] = useState<ChartHitoItem | null>(null);

  const handleDeleteHito = async (h: ChartHitoItem) => {
    if (!h.id || !clientSlug) return;
    if (!window.confirm(`¿Seguro que deseas eliminar el hito "${h.title}"?`)) return;
    try {
      await deleteClientHito({ id_item: h.id, client_slug: clientSlug });
      setPinnedIdx(null);
      setHoveredIdx(null);
      setIsHoveringTooltip(false);
      onHitoCreated?.();
    } catch (err: any) {
      alert(err?.message || "No fue posible eliminar el hito");
    }
  };

  if (!cleanData.length) return <div ref={containerRef} className="chart-empty">Sin datos de eficiencia para el periodo.</div>;

  const height = 280;
  const margin = { top: 26, right: 76, bottom: 48, left: 70 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  const maxClicks = niceAxisMax(Math.max(...cleanData.map((row) => number(row.clics)), 1));
  const maxConv = niceAxisMax(Math.max(...cleanData.map((row) => number(row.conversions)), 1));
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const labelStep = Math.max(1, Math.ceil(cleanData.length / 8));

  const xAt = (index: number) => cleanData.length === 1
    ? margin.left + plotWidth / 2
    : margin.left + (index / (cleanData.length - 1)) * plotWidth;

  const yClicks = (value: number) => margin.top + plotHeight - (maxClicks > 0 ? (value / maxClicks) * plotHeight : 0);
  const yConv = (value: number) => margin.top + plotHeight - (maxConv > 0 ? (value / maxConv) * plotHeight : 0);

  const lineClicks = cleanData.map((row, index) => `${index === 0 ? "M" : "L"} ${xAt(index)} ${yClicks(number(row.clics))}`).join(" ");
  const lineConv = cleanData.map((row, index) => `${index === 0 ? "M" : "L"} ${xAt(index)} ${yConv(number(row.conversions))}`).join(" ");

  const executedActions = useMemo(() => mapActionsToDataPoints(actions, cleanData), [actions, cleanData]);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (pinnedIdx !== null || isHoveringTooltip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    if (mouseX < margin.left || mouseX > width - margin.right) {
      setHoveredIdx(null);
      return;
    }
    let closestIdx = 0;
    let minDist = Infinity;
    cleanData.forEach((_, idx) => {
      const px = xAt(idx);
      const dist = Math.abs(px - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closestIdx = idx;
      }
    });
    setHoveredIdx(closestIdx);
    setMousePos({ x: xAt(closestIdx), y: Math.max(margin.top, Math.min(mouseY, height - margin.bottom)) });
  };

  const activeIdx = pinnedIdx !== null ? pinnedIdx : hoveredIdx;
  const activeRow = activeIdx !== null ? cleanData[activeIdx] : null;
  const activeDate = activeRow ? String(activeRow.date || "").slice(0, 10) : "";
  const activeHitos = activeDate
    ? actions
        .filter((a) => a.executed_at && String(a.executed_at).slice(0, 10) === activeDate)
        .map((a) => ({
          id: a.id_item ? String(a.id_item) : (a.id ? String(a.id) : undefined),
          title: String(a.title || a.titulo || "Hito ejecutado"),
          date: String(a.executed_at).slice(0, 10),
          comment: a.comentario ? String(a.comentario) : undefined,
          category: a.category || a.dominio ? String(a.category || a.dominio) : undefined,
        }))
    : [];

  return (
    <div className="paid-series-wrap">
      <div className="paid-series-meta" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="paid-series-legend" style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <span><i className="is-clicks" style={{ borderColor: "#6f8cff" }} />Clics · eje izquierdo</span>
          <span><i className="is-impressions" style={{ borderColor: "#ff5f68" }} />Conversiones · eje derecho</span>
          {executedActions.length > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: "#38bdf8", fontWeight: 600 }}>
              <span>🚩</span> Hito ejecutado
            </span>
          )}
        </div>
        {clientSlug && canManageHitos && (
          <button
            type="button"
            className="button button-ghost button-small"
            onClick={() => {
              setEditingHito(null);
              setIsHitoModalOpen(true);
            }}
            style={{ fontSize: "11px", display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 8px" }}
          >
            <Flag size={12} color="#38bdf8" />
            <span>Registrar hito</span>
          </button>
        )}
      </div>
      <div
        className="time-chart-scroll"
        ref={containerRef}
        onMouseLeave={() => {
          if (pinnedIdx === null && !isHoveringTooltip) {
            setHoveredIdx(null);
          }
        }}
      >
        <svg
          className="time-chart paid-efficiency-chart"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Evolución de clics y conversiones en líneas"
          onMouseMove={handleMouseMove}
          onClick={() => {
            if (pinnedIdx !== null) {
              setPinnedIdx(null);
            } else if (hoveredIdx !== null) {
              setPinnedIdx(hoveredIdx);
            }
          }}
          style={{ cursor: "crosshair" }}
        >
          {ticks.map((ratio) => {
            const y = margin.top + plotHeight * (1 - ratio);
            return (
              <g key={ratio}>
                <line className="chart-grid-line" x1={margin.left} x2={width - margin.right} y1={y} y2={y} />
                <text className="chart-axis-label is-clicks" x={margin.left - 10} y={y} textAnchor="end" dominantBaseline="middle">{compactNumber(maxClicks * ratio)}</text>
                <text className="chart-axis-label is-impressions" x={width - margin.right + 10} y={y} textAnchor="start" dominantBaseline="middle">{compactNumber(maxConv * ratio)}</text>
              </g>
            );
          })}
          <text className="chart-axis-title is-clicks" x={12} y={13} fill="#6f8cff">CLICS</text>
          <text className="chart-axis-title is-impressions" x={width - 12} y={13} textAnchor="end" fill="#ff5f68">CONVERSIONES</text>

          {/* Línea de crosshair activa al hacer hover o pin */}
          {activeIdx !== null && (
            <line
              className="chart-crosshair-line"
              x1={xAt(activeIdx)}
              x2={xAt(activeIdx)}
              y1={margin.top}
              y2={margin.top + plotHeight}
            />
          )}

          {/* Banderas de Hitos */}
          {executedActions.map((action, i) => {
            if (action.idx === null || action.idx === undefined) return null;
            const x = xAt(action.idx);
            const actAny = action as any;
            return (
              <g
                key={actAny.id || actAny.id_item ? String(actAny.id || actAny.id_item) : i}
                className="chart-hito-flag"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setPinnedIdx(action.idx);
                  setHoveredIdx(action.idx);
                  setMousePos({ x, y: margin.top + 20 });
                }}
              >
                <line
                  x1={x}
                  x2={x}
                  y1={margin.top}
                  y2={margin.top + plotHeight}
                  stroke="#38bdf8"
                  strokeWidth="1.5"
                  strokeDasharray="4 3"
                />
                <circle cx={x} cy={margin.top - 7} r="10" fill="rgba(56, 189, 248, 0.18)" />
                <g transform={`translate(${x - 6}, ${margin.top - 15})`}>
                  <line x1="2" y1="2" x2="2" y2="16" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" />
                  <path d="M2 2 L12 5.5 L2 9 Z" fill="#38bdf8" stroke="#0284c7" strokeWidth="1" strokeLinejoin="round" />
                </g>
              </g>
            );
          })}

          <path d={lineClicks} fill="none" stroke="#6f8cff" strokeWidth="2.5" opacity={activeIdx !== null ? 0.85 : 1} />
          <path d={lineConv} fill="none" stroke="#ff5f68" strokeWidth="2.5" opacity={activeIdx !== null ? 0.85 : 1} />

          {cleanData.map((row, index) => {
            const x = xAt(index);
            const showLabel = cleanData.length <= 12 || index === 0 || index === cleanData.length - 1 || index % labelStep === 0;
            return (
              <g key={`${row.date}-${index}`}>
                <circle cx={x} cy={yClicks(number(row.clics))} r="3" fill="#6f8cff" stroke="#12141c" strokeWidth="1.5" />
                <circle cx={x} cy={yConv(number(row.conversions))} r="3" fill="#ff5f68" stroke="#12141c" strokeWidth="1.5" />
                {showLabel && <text className="chart-x-label" x={x} y={height - 17} textAnchor="middle">{shortDateLabel(row.date)}</text>}
              </g>
            );
          })}

          {/* Highlight circles on hover/pin */}
          {activeIdx !== null && (
            <>
              <circle
                className="chart-active-point"
                cx={xAt(activeIdx)}
                cy={yClicks(number(cleanData[activeIdx].clics))}
                r={5.5}
                fill="#6f8cff"
              />
              <circle
                className="chart-active-point"
                cx={xAt(activeIdx)}
                cy={yConv(number(cleanData[activeIdx].conversions))}
                r={5.5}
                fill="#ff5f68"
              />
            </>
          )}
        </svg>

        {/* Tooltip interactivo estilo Semrush */}
        {activeRow && (
          <ChartTooltip
            visible={activeIdx !== null}
            x={mousePos.x}
            y={mousePos.y}
            title={formatTooltipDate(activeRow.date)}
            items={[
              { color: "#6f8cff", label: "Clics", value: formatValue(activeRow.clics, "integer") },
              { color: "#ff5f68", label: "Conversiones", value: formatValue(activeRow.conversions, "decimal") },
              { color: "var(--color-text-secondary, #94a3b8)", label: "Costo", value: formatValue(activeRow.costo_total, "currency") },
              { color: "var(--color-text-secondary, #94a3b8)", label: "CPC", value: formatValue(activeRow.cpc_dia, "currency") },
            ]}
            hitos={activeHitos}
            containerWidth={width}
            containerHeight={height}
            canManageHitos={canManageHitos}
            isPinned={pinnedIdx !== null}
            onClose={() => {
              setPinnedIdx(null);
              setHoveredIdx(null);
              setIsHoveringTooltip(false);
            }}
            onMouseEnter={() => setIsHoveringTooltip(true)}
            onMouseLeave={() => {
              setIsHoveringTooltip(false);
              if (pinnedIdx === null) setHoveredIdx(null);
            }}
            onEditHito={(h) => {
              setEditingHito(h);
              setIsHitoModalOpen(true);
            }}
            onDeleteHito={handleDeleteHito}
          />
        )}
      </div>

      {clientSlug && (
        <RegisterHitoModal
          isOpen={isHitoModalOpen}
          onClose={() => {
            setIsHitoModalOpen(false);
            setEditingHito(null);
          }}
          clientSlug={clientSlug}
          initialDomain="Paid"
          initialHito={editingHito}
          onHitoCreated={onHitoCreated}
        />
      )}
    </div>
  );
}

type QueueSeverity = "critical" | "warn" | "info";

function actionSeverity(action: unknown): QueueSeverity {
  if (action && typeof action === "object" && !Array.isArray(action)) {
    const priority = text((action as DataRecord).priority, "").toLowerCase();
    if (priority === "critical") return "critical";
    if (priority === "high") return "warn";
    return "info";
  }
  const raw = String(action ?? "");
  if (/^\s*🔴/.test(raw)) return "critical";
  if (/^\s*🟡/.test(raw)) return "warn";
  return "info";
}

function actionSeverityBadge(sev: QueueSeverity, category?: unknown): string {
  const cat = text(category, "");
  if (cat) return cat.charAt(0).toUpperCase() + cat.slice(1);
  return sev === "critical" ? "Crítico" : sev === "warn" ? "Revisar" : "Hallazgo";
}

/** Objetos estructurados traen title/impact; el texto libre de Gertrudis se
 *  parte en la primera oración para simular el mismo par título/detalle. */
function actionTitleAndBody(action: unknown): { title: string; body: string } {
  if (action && typeof action === "object" && !Array.isArray(action) && "title" in (action as DataRecord)) {
    const obj = action as DataRecord;
    return { title: text(obj.title), body: text(obj.impact, "") };
  }
  const raw = String(action ?? "").trim();
  const stripped = raw.replace(/^(?:🔴|🟡|🟢|⚪|⚫|🔵)\s*\*?Diagnóstico:?\*?\s*/i, "");
  const breakMatch = stripped.match(/[.:]\s|\n/);
  const breakIndex = breakMatch && breakMatch.index != null ? breakMatch.index + breakMatch[0].length : -1;
  if (breakIndex > 0 && breakIndex < 140) {
    return { title: stripped.slice(0, breakIndex).replace(/\*\*/g, "").trim(), body: stripped.slice(breakIndex).trim() };
  }
  const title = stripped.length > 90 ? `${stripped.slice(0, 90).trim()}…` : stripped;
  return { title, body: stripped };
}

function deltaNote(pct: number | null): string | undefined {
  if (pct == null || !Number.isFinite(pct)) return undefined;
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}% vs. período anterior`;
}

function deltaTone(pct: number | null): "up" | "down" | "flat" {
  if (pct == null || !Number.isFinite(pct) || Math.abs(pct) < 0.05) return "flat";
  return pct > 0 ? "up" : "down";
}

/** Guarda contra "0", "0.0%" o "—": si el valor formateado cae en cero
 *  permanente, la fila no se agrega -- no se renderiza vacía. */
function isEmptyMetricValue(formatted: string): boolean {
  return formatted === "—" || formatted === "0" || formatted === "0.0%" || formatted === "$0";
}

function VerdictBanner({ tone, tag, title, body }: { tone: "ok" | "bad" | "warn"; tag: string; title: string; body: string }) {
  return <div className={`paid-verdict tone-${tone}`}>
    <span className="paid-verdict-tag">{tag}</span>
    <div className="paid-verdict-body">
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  </div>;
}

/** Real-automático nunca lleva insignia -- es el default y el caso de hoy
 *  (Motherna). Importado y modelado sí, para que jamás se lean como el mismo
 *  tipo de número: uno es plata real con demora, el otro es una estimación. */
function ValueSourceBadge({ badge }: { badge?: string | null }) {
  if (!badge) return null;
  const tone = badge === "Modelado" ? "modeled" : "imported";
  return <span className={`paid-value-badge is-${tone}`}>{badge}</span>;
}

function HeroMetric({ label, value, unit, deltaText, tone, badge }: {
  label: string; value: string; unit: string; deltaText?: string; tone?: "up" | "down" | "flat"; badge?: string | null;
}) {
  return <div className="panel paid-hero">
    <span className="eyebrow">{label}</span>
    <div className="paid-hero-figure">
      <span className="paid-hero-value num">{value}</span>
      <span className="paid-hero-unit">{unit}</span>
      <ValueSourceBadge badge={badge} />
    </div>
    {deltaText && <span className={`paid-hero-delta is-${tone ?? "flat"}`}>{deltaText}</span>}
  </div>;
}

function PendingPanel({ items }: { items: Array<{ name: string; missing: string; reason: string }> }) {
  return <div className="paid-pending-panel">
    <h3>{items.length > 0 ? `${items.length} métrica${items.length > 1 ? "s" : ""} esperan configuración` : "Ninguna métrica está esperando configuración"}</h3>
    <p>{items.length > 0
      ? "Aparecen solas cuando el dato que necesitan exista. Se declaran en vez de mostrarse en cero."
      : "Todas las métricas de esta cuenta tienen el dato que necesitan para esta ventana."}</p>
    {items.length > 0 && <div className="paid-pending-grid">
      {items.map((item) => <div className="paid-pending-item" key={item.name}>
        <div className="paid-pending-name">{item.name}</div>
        <div className="paid-pending-missing num">{item.missing}</div>
        <p className="paid-pending-reason">{item.reason}</p>
      </div>)}
    </div>}
  </div>;
}

/** La cola de acción de Gertrudis: severidad como franja izquierda, pasos
 *  plegados en <details>. Vive sobre el pliegue -- primer elemento
 *  interactivo del tab, antes de cualquier tarjeta de métrica. */
function PaidActionQueue({ audit, busy, error, onRun, label, target }: {
  audit?: GertrudisAuditPayload;
  busy: boolean;
  error?: string | null;
  onRun: () => void;
  label: string;
  target: Omit<FeedbackTarget, "insight">;
}) {
  const insight = audit?.insight?.trim();
  const history = audit?.history ?? [];
  const previousHistory = history.slice(1);
  const fresh = Boolean(insight) && !audit?.id;

  let actionBlocks: unknown[] = audit?.actions?.length ? audit.actions : [];
  if (!actionBlocks.length && insight) {
    const chunks = insight.split(/(?=(?:🔴|🟡|🟢|⚪|⚫|🔵)\s*\*?Diagnóstico:?\*?)/i).map((c) => c.trim()).filter(Boolean);
    actionBlocks = chunks.length > 1 ? chunks : [insight];
  }

  return <div className="paid-queue-wrap">
    <div className="paid-queue-head">
      <span className="eyebrow">{actionBlocks.length > 0 ? `Cola de acción · ${actionBlocks.length} abierta${actionBlocks.length > 1 ? "s" : ""}` : "Cola de acción"}</span>
      <div className="paid-queue-controls">
        <button className="button button-primary gertrudis-run" type="button" disabled={busy} onClick={onRun}>
          {busy ? <><span className="button-spinner" /> Gertrudis está analizando…</> : <><BrainCircuit size={16} /> {insight ? "Generar nueva auditoría" : label}</>}
        </button>
        {insight && <span className={`audit-stamp${fresh ? " is-fresh" : ""}`}>
          <Clock size={14} />
          <span>Auditoría del <strong>{formatAuditDate(audit?.timestamp)}</strong></span>
          <i>{fresh ? "recién generada" : "cargada desde el historial"}</i>
        </span>}
      </div>
    </div>
    {error && <div className="gertrudis-error">{error}</div>}
    {!insight && !busy && <p className="paid-queue-empty">Todavía no existe una auditoría persistida para esta cuenta. Generarla es el primer paso de la cola de acción.</p>}
    {actionBlocks.length > 0 && <div className="paid-queue">
      {actionBlocks.map((action, index) => {
        const sev = actionSeverity(action);
        const badge = actionSeverityBadge(sev, (action as DataRecord)?.category);
        const { title, body } = actionTitleAndBody(action);
        return <details className={`paid-queue-item is-${sev}`} key={index} open={index === 0}>
          <summary>
            <span className={`paid-queue-sev is-${sev}`}>{badge}</span>
            <span className="paid-queue-item-head">
              <p className="paid-queue-item-title">{title}</p>
            </span>
            <ChevronRight className="paid-queue-chev" size={15} />
          </summary>
          <div className="paid-queue-detail">
            {body && <div className="gertrudis-response-copy" dangerouslySetInnerHTML={{ __html: renderSimpleMarkdown(body) }} />}
            <AuditFeedback target={{ ...target, insight: `${title}: ${body}` }} title="¿Te sirve esta acción?" />
          </div>
        </details>;
      })}
    </div>}
    {previousHistory.length > 0 && <details className="audit-history-disclosure">
      <summary>Ver auditorías anteriores <span>{previousHistory.length}</span></summary>
      <div className="audit-history-list">
        {previousHistory.map((item, index) => <details key={item.id ?? `${item.timestamp}-${index}`}>
          <summary><span>Auditoría {previousHistory.length - index}</span><time>{formatAuditDate(item.timestamp)}</time></summary>
          <AuditBody
            audit={item}
            target={{ ...target, level: item.level ?? audit?.level, element: item.element ?? audit?.element }}
            actionsTitle="Acciones de esa corrida"
          />
        </details>)}
      </div>
    </details>}
  </div>;
}

function PaidPacing({ data, clientSlug, onAuditGenerated, activePlatform, onPlatformChange, onHitoCreated }: {
  data: PaidDetailPayload;
  clientSlug: string;
  onAuditGenerated: (audit: GertrudisAuditPayload) => void;
  activePlatform?: string;
  onPlatformChange?: (platform: string) => void;
  onHitoCreated?: () => void;
}) {
  const { data: dashboardData } = useDashboard();
  const clientHealth = dashboardData?.health[clientSlug];
  const platforms = useMemo(() => {
    const fromPacing = (data.pacing ?? []).map((row) => text(row.platform).toUpperCase());
    const fromDaily = (data.daily ?? []).map((row) => text(row.platform).toUpperCase());
    const fromLifetime = (data.lifetime ?? []).map((row) => text(row.platform).toUpperCase());
    const fromCampaigns = (data.dailyCampaigns ?? []).map((row) => text(row.platform).toUpperCase());
    const fromHealth = (clientHealth?.paidPlatforms ?? []).map((item) => text(item.platform).toUpperCase());
    const unique = [...new Set([...fromPacing, ...fromDaily, ...fromLifetime, ...fromCampaigns, ...fromHealth])].filter(Boolean);
    const prio: Record<string, number> = { GADS: 1, META: 2, LINKEDIN: 3, TIKTOK: 4, PINTEREST: 5 };
    return unique.sort((a, b) => (prio[a] ?? 99) - (prio[b] ?? 99));
  }, [data.pacing, data.daily, data.lifetime, data.dailyCampaigns, clientHealth]);
  const defaultPlatform = platforms.includes("GADS") ? "GADS" : platforms[0] ?? "ALL";
  const [internalPlatform, setInternalPlatform] = useState(defaultPlatform);
  const platform = activePlatform ?? internalPlatform;
  const setPlatform = onPlatformChange ?? setInternalPlatform;
  const accountOptions = useMemo(() => {
    const list = [...(data.pacing ?? []), ...(data.daily ?? [])]
      .filter((row) => text(row.platform).toUpperCase() === platform)
      .map((row) => ({ id: text(row.account_id, ""), name: text(row.account_name, text(row.account_id)) }))
      .filter((item) => item.id);
    const seen = new Set<string>();
    return list.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [data.pacing, data.daily, platform]);
  const [accountFilter, setAccountFilter] = useState("ALL");
  useEffect(() => {
    if (!activePlatform) {
      setInternalPlatform(platforms.includes("GADS") ? "GADS" : platforms[0] ?? "ALL");
    }
  }, [platforms.join("|"), activePlatform]);
  useEffect(() => { setAccountFilter("ALL"); }, [platform]);
  const inScope = (row: DataRecord) => (platform === "ALL" || text(row.platform).toUpperCase() === platform) && (accountFilter === "ALL" || text(row.account_id) === accountFilter);
  const pacing = (data.pacing ?? []).filter(inScope);
  const daily = (data.daily ?? []).filter(inScope);
  const dailySeries = useMemo(
    () => aggregatePaidDaily(daily, data.window?.period_start, data.window?.period_end),
    [daily, data.window?.period_start, data.window?.period_end],
  );
  const lifetime = (data.lifetime ?? []).filter(inScope);
  const dailyCampaigns = (data.dailyCampaigns ?? []).filter(inScope);
  const isMtd = text(data.window?.window) === "MTD";
  const summary = record(data.summary);
  const previous = record(data.previous);
  const spend = isMtd ? pacing.reduce((sum, row) => sum + number(row.spend_mtd), 0) : daily.reduce((sum, row) => sum + number(row.spend_daily), 0);
  const budget = pacing.reduce((sum, row) => sum + number(row.budget_month_est), 0);
  const conversions = isMtd ? pacing.reduce((sum, row) => sum + number(row.conversions_mtd), 0) : daily.reduce((sum, row) => sum + number(row.conversions_daily), 0);
  const conversionValue = isMtd ? pacing.reduce((sum, row) => sum + number(row.conversion_value_mtd), 0) : daily.reduce((sum, row) => sum + number(row.conversion_value_daily), 0);
  const previousSpend = number(previous.spend_total);
  const spendDelta = previousSpend ? (number(summary.spend_total) / previousSpend - 1) * 100 : null;
  const organicDelta = data.organicPrevious ? (number(data.organicTraffic) / number(data.organicPrevious) - 1) * 100 : null;
  const totalClicks = (data.cpc ?? []).reduce((sum, row) => sum + number(row.clics), 0);
  const cpcAvg = totalClicks > 0 ? spend / totalClicks : 0;
  const cpa = conversions > 0 ? spend / conversions : null;
  const roas = spend > 0 && conversionValue > 0 ? conversionValue / spend : null;

  const previousConversions = number(previous.conversions);
  const conversionsDelta = previousConversions ? (number(summary.conversions) / previousConversions - 1) * 100 : null;
  const previousRoas = previous.roas != null ? number(previous.roas) : null;
  const roasDelta = previousRoas && summary.roas != null ? (number(summary.roas) / previousRoas - 1) * 100 : null;
  const previousCpa = previous.cpa != null ? number(previous.cpa) : null;
  const cpaDelta = previousCpa && summary.cpa != null ? (number(summary.cpa) / previousCpa - 1) * 100 : null;

  /* Las métricas de portada salen del objetivo declarado del cliente, no de una
     lista fija. ROAS y valor de conversión solo significan algo cuando el valor
     lo pone el carro de compra (familia VENTA); en objetivos de lead el valor de
     conversión es una casilla que alguien llenó a mano, y el ratio miente. */
  const { data: dashboard } = useDashboard();
  const objective = dashboard?.clients.find((item) => item.clientSlug === clientSlug)?.objective;
  const objectiveDeclared = Boolean(objective?.declared && objective?.primary);
  const isSalesGoal = text(objective?.primary?.familia).toUpperCase() === "VENTA";
  const goalUnitPlural = text(objective?.primary?.unidad) || "conversiones";
  const goalUnit = singularize(goalUnitPlural);

  /* Un target inexistente se imprimía como "target ≤ $0". Un campo vacío no es
     una meta: si no hay fila propia del cliente, se dice que falta. */
  const clientSpecificParams = Array.isArray(data.params?._client_specific) ? (data.params._client_specific as string[]) : null;
  const isParamClientSpecific = (paramName: string) => clientSpecificParams === null || clientSpecificParams.includes(paramName);
  const cpaTarget = isParamClientSpecific("CPA_MAX") && number(data.params?.CPA_MAX) > 0 ? number(data.params?.CPA_MAX) : null;
  const roasTarget = isParamClientSpecific("ROAS_MIN") && number(data.params?.ROAS_MIN) > 0 ? number(data.params?.ROAS_MIN) : null;
  const targetNote = (value: string | null) => value ?? "sin target declarado";

  /* Real y automático, real e importado, o modelado -- son tres orígenes
     distintos del valor de conversión y nunca se muestran igual. Sin el
     parámetro declarado, el default es real-automático (el caso de hoy,
     Motherna) para no cambiarle el comportamiento a nadie. Un valor
     modelado en un objetivo de contacto (Mora Pavic, agendamientos) es un
     dato de apoyo para Google Ads, nunca la portada ni un ROAS. */
  const conversionValueSourceRaw = text(data.params?.VALOR_CONVERSION_ORIGEN, "").toUpperCase();
  const conversionValueSource: "REAL_AUTOMATICO" | "REAL_IMPORTADO" | "MODELADO" =
    conversionValueSourceRaw === "MODELADO" ? "MODELADO"
    : conversionValueSourceRaw === "REAL_IMPORTADO" ? "REAL_IMPORTADO"
    : "REAL_AUTOMATICO";
  const conversionValueBadge = conversionValueSource === "MODELADO" ? "Modelado"
    : conversionValueSource === "REAL_IMPORTADO" ? "Importado"
    : null;
  const modeledValuePerConversion = !isSalesGoal && number(data.params?.VALOR_CONVERSION_MODELADO) > 0
    ? number(data.params?.VALOR_CONVERSION_MODELADO)
    : null;

  const account = pacing[0] ?? daily[0] ?? (data.pacing ?? []).find((p) => text(p.platform).toUpperCase() === platform) ?? (data.daily ?? []).find((d) => text(d.platform).toUpperCase() === platform);
  const platformRaw = text(account?.platform, platform);
  const accountId = text(account?.account_id, "");
  const persistedAudit = (data.gertrudisAudits ?? []).find((item) =>
    text(item.platform).toUpperCase() === platform && text(item.accountId, "") === accountId,
  ) as GertrudisAuditPayload | undefined;
  const [gertrudisBusy, setGertrudisBusy] = useState(false);
  const [gertrudisError, setGertrudisError] = useState<string | null>(null);
  const [showPaidUsedData, setShowPaidUsedData] = useState(false);
  
  const [balance, setBalance] = useState<number | null>(null);
  useEffect(() => {
    fetch("/api/wallet/")
      .then((r) => r.json())
      .then((d) => setBalance(d.balance))
      .catch((e) => console.error("Error fetching balance:", e));
  }, []);

  useEffect(() => { setGertrudisError(null); }, [clientSlug, platform, accountId]);
  const runPaidAudit = async () => {
    if (balance !== null && balance < 1) {
      setGertrudisError("Saldo insuficiente. Necesitas 1 token para la auditoría Paid.");
      return;
    }
    setGertrudisBusy(true);
    setGertrudisError(null);
    try {
      const res = await fetch("/api/wallet/consume", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feature_id: "paid", cost: 1, name: "Gertrudis - Paid Media" })
      });
      if (!res.ok) {
        throw new Error("No pudimos descontar los tokens. Verifica tu saldo.");
      }
      const walletData = await res.json();
      setBalance(walletData.balance);
      
      const audit = await runGertrudisPaid(clientSlug, platformRaw, accountId || undefined);
      const previousLatest = persistedAudit?.insight ? { ...persistedAudit, history: undefined } : null;
      const history = [audit, ...(persistedAudit?.history ?? (previousLatest ? [previousLatest] : []))];
      onAuditGenerated({ ...audit, history, platform: platformRaw, accountId: accountId || null });
      setShowPaidUsedData(true);
    } catch (cause: any) {
      setGertrudisError(cause instanceof Error ? cause.message : (cause.message || "No fue posible invocar a Gertrudis."));
    } finally {
      setGertrudisBusy(false);
    }
  };

  const formatWindow = () => {
    if (!data.window?.period_start || !data.window?.period_end) return "";
    const startParts = String(data.window.period_start).split("-");
    const endParts = String(data.window.period_end).split("-");
    if (startParts.length !== 3 || endParts.length !== 3) return "";
    const months = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
    return `(${parseInt(startParts[2], 10)} - ${parseInt(endParts[2], 10)} ${months[parseInt(endParts[1], 10) - 1]})`;
  };

  const spendLabel = isMtd ? `Spend ${formatWindow()}` : `Inversión ${formatWindow()}`;

  /* Veredicto: una frase antes que los números sueltos. Reglas genéricas
     sobre pacing/objetivo/retorno -- nunca texto fijo por cliente. */
  const pacingPct = budget > 0 ? spend / budget : null;
  const overBudget = pacingPct != null && pacingPct > 1;
  const roasQualifier = conversionValueBadge ? ` (retorno ${conversionValueBadge.toLowerCase()})` : "";
  const verdict = (() => {
    if (!objectiveDeclared) {
      return {
        tone: "bad" as const,
        tag: "Sin objetivo",
        title: "Sin objetivo declarado: el tablero no puede decir si esto va bien o mal.",
        text: `${formatValue(spend, "currency")} invertidos${cpa != null ? ` y costo por conversión ${formatValue(cpa, "currency")}` : ""}. Falta declarar con el KAM qué cuenta como éxito para este cliente antes de fijar una meta de referencia.`,
      };
    }
    if (overBudget && isSalesGoal && roas != null && roasTarget != null && roas >= roasTarget) {
      return {
        tone: "ok" as const,
        tag: "Presupuesto corto",
        title: `Devuelve ${roas.toFixed(2)}×${roasQualifier} por cada $1. El presupuesto quedó chico — la cuenta no se desbordó.`,
        text: `${(pacingPct! * 100).toFixed(1)}% del presupuesto consumido con retorno ${roas.toFixed(2)}× sobre una meta de ${roasTarget.toFixed(2)}×.`,
      };
    }
    if (overBudget) {
      return {
        tone: "bad" as const,
        tag: "Sobre presupuesto",
        title: `La cuenta gastó ${(pacingPct! * 100).toFixed(1)}% del presupuesto mensual.`,
        text: `${formatValue(spend, "currency")} sobre ${formatValue(budget, "currency")} presupuestados${roas != null ? `, con retorno ${roas.toFixed(2)}×` : ""}${roasTarget != null && roas != null && roas < roasTarget ? ` bajo la meta de ${roasTarget.toFixed(2)}×` : ""}.`,
      };
    }
    if (isSalesGoal && roasTarget != null && roas != null && roas < roasTarget) {
      return {
        tone: "bad" as const,
        tag: "Bajo retorno",
        title: `ROAS ${roas.toFixed(2)}×${roasQualifier} no alcanza la meta de ${roasTarget.toFixed(2)}×.`,
        text: `${formatValue(spend, "currency")} invertidos devolvieron ${formatValue(conversionValue, "currency")}. El presupuesto está en línea; el retorno no.`,
      };
    }
    if (!isSalesGoal && cpaTarget != null && cpa != null && cpa > cpaTarget) {
      return {
        tone: "bad" as const,
        tag: "Sobre costo objetivo",
        title: `Costo por ${goalUnit} en ${formatValue(cpa, "currency")}, sobre la meta de ${formatValue(cpaTarget, "currency")}.`,
        text: `Presupuesto en línea. Costo por ${goalUnit} sobre la meta acordada.`,
      };
    }
    if (cpa == null && conversions === 0) {
      return {
        tone: "warn" as const,
        tag: "En presupuesto",
        title: "La inversión va en línea. Falta un paso para medir el objetivo del cliente.",
        text: `Pacing ${pacingPct != null ? `${(pacingPct * 100).toFixed(1)}%` : "sano"} con presupuesto sano. Sin ${goalUnitPlural} registradas todavía, no hay forma de validar el retorno.`,
      };
    }
    return {
      tone: "ok" as const,
      tag: "En presupuesto",
      title: "La cuenta va en línea con el presupuesto y el objetivo declarado.",
      text: `Pacing ${pacingPct != null ? `${(pacingPct * 100).toFixed(1)}%` : "sano"}${cpa != null ? `, costo por ${goalUnit} ${formatValue(cpa, "currency")}` : ""}${cpaTarget != null && cpa != null ? ` contra una meta de ${formatValue(cpaTarget, "currency")}` : ""}.`,
    };
  })();

  /* Métrica de portada: ROAS cuando el objetivo es venta y hay retorno real;
     si no, el conteo de la unidad declarada. Nunca en cero: si no hay
     conversiones todavía, se declara en vez de imprimir "0". */
  const heroIsRoas = isSalesGoal && roas != null;
  const heroLabel = heroIsRoas ? "Métrica de portada · Retorno" : `Métrica de portada · ${objectiveDeclared ? goalUnitPlural.charAt(0).toUpperCase() + goalUnitPlural.slice(1) : "Conversiones"}`;
  const heroEmpty = !heroIsRoas && conversions === 0;
  const heroValue = heroIsRoas ? `${(roas as number).toFixed(2)}×` : formatValue(conversions, "decimal");
  const heroUnit = heroIsRoas ? "por cada $1 invertido" : (objectiveDeclared ? goalUnitPlural : "conversiones");
  const heroDeltaPct = heroIsRoas ? roasDelta : conversionsDelta;

  /* Filas de apoyo: solo las que tienen dato real. Lo que falta se declara
     en el panel de pendientes, nunca como "—" o "0" en una fila. */
  type PaidRow = { label: string; value: string; note?: string; badge?: string | null };
  const rows: PaidRow[] = [];
  const spendFormatted = formatValue(spend, "currency");
  if (!isEmptyMetricValue(spendFormatted)) rows.push({ label: spendLabel, value: spendFormatted, note: deltaNote(spendDelta) });
  const clicksFormatted = formatValue(totalClicks, "integer");
  if (!isEmptyMetricValue(clicksFormatted)) rows.push({ label: "Clics", value: clicksFormatted });
  const cpcFormatted = formatValue(cpcAvg, "currency");
  if (!isEmptyMetricValue(cpcFormatted)) rows.push({ label: "CPC promedio", value: cpcFormatted });
  if (isSalesGoal) {
    const revFormatted = formatValue(conversionValue, "currency");
    if (!isEmptyMetricValue(revFormatted)) rows.push({
      label: "Ingresos atribuidos",
      value: revFormatted,
      note: conversions > 0 ? `ticket promedio ${formatValue(conversionValue / conversions, "currency")}` : undefined,
      badge: conversionValueBadge,
    });
  }
  if (heroIsRoas) {
    const convFormatted = formatValue(conversions, "decimal");
    if (!isEmptyMetricValue(convFormatted)) rows.push({ label: objectiveDeclared ? goalUnitPlural.charAt(0).toUpperCase() + goalUnitPlural.slice(1) : "Conversiones", value: convFormatted, note: deltaNote(conversionsDelta) });
  }
  if (cpa != null) {
    const cpaFormatted = formatValue(cpa, "currency");
    if (!isEmptyMetricValue(cpaFormatted)) rows.push({
      label: objectiveDeclared ? `Costo por ${goalUnit}` : "CPA",
      value: cpaFormatted,
      note: [deltaNote(cpaDelta), objectiveDeclared ? targetNote(cpaTarget == null ? null : `meta ≤ ${formatValue(cpaTarget, "currency")}`) : undefined].filter(Boolean).join(" · ") || undefined,
    });
  }
  if (isSalesGoal) {
    const budgetFormatted = formatValue(budget, "currency");
    if (!isEmptyMetricValue(budgetFormatted)) rows.push({ label: "Budget mensual", value: budgetFormatted });
  }
  if (budget > 0) {
    const pacingFormatted = `${(spend / budget * 100).toFixed(1)}%`;
    if (!isEmptyMetricValue(pacingFormatted)) rows.push({
      label: "Pacing del presupuesto",
      value: pacingFormatted,
      note: !isSalesGoal ? `de ${formatValue(budget, "currency")}` : undefined,
    });
  }
  if (isSalesGoal && !heroIsRoas && roas != null) {
    rows.push({ label: "ROAS", value: `${roas.toFixed(2)}×`, note: targetNote(roasTarget == null ? null : `meta ≥ ${roasTarget.toFixed(2)}×`), badge: conversionValueBadge });
  }

  /* Pendientes: lo que no se puede calcular todavía, declarado con lo que
     falta -- nunca una tarjeta en cero. */
  const pendingItems: Array<{ name: string; missing: string; reason: string }> = [];
  if (cpa == null) {
    pendingItems.push({
      name: objectiveDeclared ? `Costo por ${goalUnit}` : "CPA",
      missing: "requiere conversiones registradas en la ventana",
      reason: "Sin conversiones contadas todavía no se puede dividir el gasto.",
    });
  }
  if (isSalesGoal && roas == null) {
    pendingItems.push({
      name: "ROAS",
      missing: "requiere valor de conversión real",
      reason: "El carro de compra no reportó ingresos en esta ventana; sin valor, el ratio no se puede calcular.",
    });
  }
  if (budget === 0) {
    pendingItems.push({
      name: "Pacing del presupuesto",
      missing: "requiere presupuesto mensual configurado",
      reason: "Sin budget_month_est cargado no hay contra qué medir el ritmo de gasto.",
    });
  }

  const gertrudisTarget = { clientSlug, domain: "MKT" as const, level: persistedAudit?.level, element: persistedAudit?.element };

  return <div className="detail-stack paid-resumen">
    <VerdictBanner tone={verdict.tone} tag={verdict.tag} title={verdict.title} body={verdict.text} />

    {account ? <PaidActionQueue
      audit={persistedAudit}
      busy={gertrudisBusy}
      error={gertrudisError}
      onRun={runPaidAudit}
      label={balance !== null && balance < 1 ? "Generar auditoría Paid (Sin saldo, requiere 1 token)" : `Generar auditoría Paid (${platform} - 1 token)`}
      target={gertrudisTarget}
    /> : <div className="detail-notice"><Info size={17} /><span>Sin filas de pacing para {platform}: Gertrudis Paid necesita al menos una cuenta con inversión en la ventana para auditar.</span></div>}

    {/* Contenedor colapsable: Datos que usó Gertrudis (Pacing & Campañas de {platform}) */}
    <div className="audit-used-data-section">
      <button
        type="button"
        className="button button-ghost audit-used-data-toggle"
        onClick={() => setShowPaidUsedData(!showPaidUsedData)}
      >
        <Sparkles size={14} />
        {showPaidUsedData ? "Ocultar datos que usó Gertrudis" : "📊 Ver datos que usó Gertrudis"}
      </button>

      {showPaidUsedData && (
        <div className="audit-used-data-card">
          <div className="audit-used-data-header">
            <h4>Insumos consolidados consumidos por Gertrudis (Pacing & Campañas de {platform})</h4>
            <span className="audit-used-data-badge">{platform} Ads + GA4</span>
          </div>

          <div className="audit-used-kpis-grid">
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Gasto en ventana</span>
              <strong>{formatValue(spend, "currency")}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Presupuesto mensual</span>
              <strong>{budget > 0 ? formatValue(budget, "currency") : "Sin presupuesto"}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Ritmo (Pacing)</span>
              <strong>{pacingPct != null ? `${(pacingPct * 100).toFixed(1)}%` : "—"}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">{goalUnitPlural}</span>
              <strong>{heroEmpty ? "0" : heroValue}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">CPA real / meta</span>
              <strong>{cpa != null ? formatValue(cpa, "currency") : "—"}{cpaTarget != null ? ` / ${formatValue(cpaTarget, "currency")}` : ""}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">ROAS real / meta</span>
              <strong>{roas != null ? `${roas.toFixed(2)}×` : "—"}{roasTarget != null ? ` / ${roasTarget.toFixed(2)}×` : ""}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Objetivo declarado</span>
              <strong>{objectiveDeclared ? goalUnit : "Sin declarar"}</strong>
            </div>
            <div className="used-kpi-pill">
              <span className="used-kpi-label">Campañas evaluadas</span>
              <strong>{data.dailyCampaigns ? data.dailyCampaigns.length : 0}</strong>
            </div>
          </div>

          {!!data.dailyCampaigns && data.dailyCampaigns.length > 0 && (
            <div className="audit-used-chips-container">
              <div className="audit-used-chips-group">
                <span className="chips-group-title">Campañas activas en la ventana:</span>
                <div className="audit-used-chips-list">
                  {data.dailyCampaigns.slice(0, 8).map((c: any, i: number) => (
                    <span className="used-chip" key={i}>
                      {text(c.campaign_name || c.campaign_id)} ({formatValue(number(c.spend), "currency")})
                    </span>
                  ))}
                  {data.dailyCampaigns.length > 8 && (
                    <span className="used-chip-more">+{data.dailyCampaigns.length - 8} más</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {Boolean((data.actions as any[])?.some((a) => a.executed_at)) && (
            <div className="audit-used-chips-container" style={{ marginTop: "14px" }}>
              <div className="audit-used-chips-group">
                <span className="chips-group-title" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>🚩</span> Hitos de gestión / contexto histórico considerados:
                </span>
                <div className="chips-list" style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "6px" }}>
                  {(data.actions as any[])
                    .filter((a) => a.executed_at)
                    .map((h: any, i: number) => (
                      <div key={i} style={{ display: "flex", alignItems: "baseline", gap: "8px", fontSize: "12px", background: "rgba(56, 189, 248, 0.08)", padding: "4px 10px", borderRadius: "6px", border: "1px solid rgba(56, 189, 248, 0.2)" }}>
                        <strong style={{ color: "#38bdf8", fontFamily: "var(--font-mono, monospace)" }}>{String(h.executed_at).slice(0, 10)}</strong>
                        <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{h.title || h.titulo}</span>
                        {h.comentario && <span style={{ color: "#94a3b8" }}>— {h.comentario}</span>}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>

    <div className="paid-lead">
      <HeroMetric
        label={heroLabel}
        value={heroEmpty ? "Sin datos" : heroValue}
        unit={heroEmpty ? `${goalUnitPlural} registradas en la ventana` : heroUnit}
        deltaText={heroEmpty ? undefined : deltaNote(heroDeltaPct)}
        tone={deltaTone(heroDeltaPct)}
        badge={heroIsRoas ? conversionValueBadge : null}
      />
      <div className="panel paid-rows-panel">
        <span className="eyebrow">El período en números</span>
        <div className="paid-rows">
          {rows.map((row) => <div className="paid-row" key={row.label}>
            <span className="paid-row-k">{row.label}</span>
            <span className="paid-row-v num">{row.value}<ValueSourceBadge badge={row.badge} />{row.note && <small className="paid-row-sub">{row.note}</small>}</span>
          </div>)}
        </div>
      </div>
    </div>

    {modeledValuePerConversion != null && <div className="paid-modeled-note">
      <span className="paid-value-badge is-modeled">Modelado</span>
      <div>
        <p className="paid-modeled-title">Valor esperado por {goalUnit}: {formatValue(modeledValuePerConversion, "currency")}</p>
        <p className="paid-modeled-body">Ticket promedio × tasa de asistencia × tasa de cierre, declarado por el KAM. Alimenta la puja de Google Ads. No es la portada ni un ROAS; la portada sigue siendo {goalUnit}.</p>
      </div>
    </div>}

    {accountOptions.length > 1 && <div className="paid-filters-row">
      <label className="account-filter">Cuenta<select value={accountFilter} onChange={(event) => setAccountFilter(event.target.value)}><option value="ALL">Todas las cuentas</option>{accountOptions.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
    </div>}

    <PendingPanel items={pendingItems} />

    <SectionPanel eyebrow="Inversión diaria" title="Gasto vs presupuesto" copy={`${text(data.window?.period_start)} → ${text(data.window?.period_end)}`}>
      <SpendBudgetChart data={dailySeries} actions={(data.actions || []) as DataRecord[]} clientSlug={clientSlug} onHitoCreated={onHitoCreated} />
    </SectionPanel>
    {pacing.length > 0 && <SectionPanel eyebrow="Cuentas" title="Pacing por cuenta" count={pacing.length}>
      <DataTable data={pacing} columns={[
        { key: "platform", label: "Plataforma" }, { key: "account_name", label: "Cuenta" },
        { key: "spend_mtd", label: "Spend MTD", format: "currency" }, { key: "budget_month_est", label: "Budget", format: "currency" },
        { key: "conversions_mtd", label: "Conv.", format: "decimal" }, { key: "status", label: "Estado", tone: (row) => statusTone(row.status) },
      ]} />
    </SectionPanel>}
    {dailyCampaigns.length > 0 && <SectionPanel eyebrow="Campañas DAILY" title="Rendimiento y pérdida de impresiones por campaña" count={dailyCampaigns.length}>
      <DataTable data={dailyCampaigns} columns={[
        { key: "campaign_name", label: "Campaña" },
        { key: "spend_mtd", label: "Gastado", format: "currency" },
        { key: "clicks", label: "Clics", format: "integer" },
        { key: "conversions", label: "Conv.", format: "decimal" },
        { key: "search_is_pct", label: "% Cuota IS", render: (row) => row.search_is_pct != null ? `${row.search_is_pct}%` : "—" },
        { key: "lost_is_budget_pct", label: "% Pérdida Presupuesto", render: (row) => row.lost_is_budget_pct != null ? `${row.lost_is_budget_pct}%` : "—", tone: (row) => Number(row.lost_is_budget_pct) >= 15 ? "accent-warning" : undefined },
        { key: "lost_is_rank_pct", label: "% Pérdida Ranking", render: (row) => row.lost_is_rank_pct != null ? `${row.lost_is_rank_pct}%` : "—", tone: (row) => Number(row.lost_is_rank_pct) >= 50 ? "accent-danger" : undefined },
        { key: "status", label: "Estado", tone: (row) => statusTone(row.status) },
      ]} />
    </SectionPanel>}
    {lifetime.length > 0 && <SectionPanel eyebrow="Campañas" title="Presupuestos Lifetime" count={lifetime.length}>
      <DataTable data={lifetime} columns={[
        { key: "campaign_name", label: "Campaña" }, { key: "platform", label: "Plataforma" },
        { key: "spend_to_date", label: "Gastado", format: "currency" }, { key: "lifetime_budget", label: "Budget lifetime", format: "currency" },
        { key: "pct_consumed", label: "% consumido", format: "percent" }, { key: "status", label: "Estado", tone: (row) => statusTone(row.status) },
      ]} empty="No hay campañas Lifetime activas o pausadas para este filtro." />
    </SectionPanel>}
    <SectionPanel eyebrow="Eficiencia" title="Clics y Conversiones" count={data.cpc?.length ?? 0}>
      <CpcClicksChart data={data.cpc ?? []} actions={(data.actions || []) as DataRecord[]} clientSlug={clientSlug} onHitoCreated={onHitoCreated} />
      <DataTable data={data.cpc ?? []} columns={[
        { key: "date", label: "Fecha" }, { key: "clics", label: "Clics", format: "integer" },
        { key: "costo_total", label: "Costo", format: "currency" }, { key: "cpc_dia", label: "CPC", format: "currency" },
        { key: "conversions", label: "Conversiones", format: "decimal" },
      ]} />
    </SectionPanel>
    <details className="panel disclosure-panel"><summary>¿Cómo se calcula “Agota en”?</summary><p>Para campañas Lifetime se divide el presupuesto remanente por el ritmo de gasto diario reciente. Es una proyección operativa: cambia con el gasto y no reemplaza la fecha configurada en plataforma.</p></details>
    <details className="panel disclosure-panel"><summary>Ver datos que Gertrudis usó</summary><div className="detail-stack"><DataTable data={pacing} columns={[{ key: "platform", label: "Plataforma" }, { key: "account_name", label: "Cuenta" }, { key: "spend_mtd", label: "Spend", format: "currency" }, { key: "budget_month_est", label: "Budget", format: "currency" }, { key: "status", label: "Estado", tone: (row) => statusTone(row.status) }]} /><DataTable data={dailyCampaigns} columns={[{ key: "campaign_name", label: "DAILY" }, { key: "spend_mtd", label: "Gastado", format: "currency" }, { key: "status", label: "Estado", tone: (row) => statusTone(row.status) }]} /><DataTable data={lifetime} columns={[{ key: "campaign_name", label: "LIFETIME" }, { key: "spend_to_date", label: "Gastado", format: "currency" }, { key: "pct_consumed", label: "% consumido", format: "percent" }]} /><DataTable data={data.gaps ?? []} columns={[{ key: "palabra_clave", label: "Gap Paid/SEO" }, { key: "Pos_Organica", label: "Pos. org.", format: "decimal" }, { key: "Costo_Mensual_Est", label: "Costo", format: "currency" }, { key: "decision", label: "Decisión" }]} /></div></details>
  </div>;
}


function Transparency({ data }: { data: TransparencyDetailPayload }) {
  const ads = data.ads ?? [];
  const advertiser = ads[0];
  const fallback = ads.some((row) => Boolean(row.is_fallback));
  const domain = (data.gscUrls?.[0] ?? "").replace(/^sc-domain:/, "").replace(/^https?:\/\//, "").split("/")[0];
  const transparencyUrl = domain ? `https://adstransparency.google.com/?domain=${encodeURIComponent(domain)}&region=CL` : "https://adstransparency.google.com/";
  return <div className="detail-stack">
    {fallback && <div className="detail-notice"><Info size={17} /><span>Este cliente no tiene un anunciante propio configurado. Se muestran creatividades de los IDs oficiales de WOLF ASESORES SPA, igual que en Streamlit.</span></div>}
    {advertiser && <div className="detail-metric-grid compact-grid"><DetailMetric label="Nombre publicado" value={text(advertiser.advertiser_disclosed_name)} /><DetailMetric label="Razón social" value={text(advertiser.advertiser_legal_name)} /><DetailMetric label="Ubicación" value={text(advertiser.advertiser_location)} /><DetailMetric label="Verificación" value={text(advertiser.advertiser_verification_status)} tone={text(advertiser.advertiser_verification_status).toUpperCase().includes("VERIF") ? "good" : "warning"} /></div>}
    <SectionPanel eyebrow="Google Ads Transparency Center" title="Creatividades publicadas" copy="Datos del último snapshot disponible en BigQuery." count={ads.length}>
      <div className="transparency-actions">
        <a className="button button-ghost" href={transparencyUrl} target="_blank" rel="noreferrer">Abrir Transparency Center <ExternalLink size={14} /></a>
      </div>
      <DataTable data={ads} columns={[
        { key: "advertiser_disclosed_name", label: "Anunciante" }, { key: "advertiser_legal_name", label: "Razón social" },
        { key: "advertiser_verification_status", label: "Verificación" }, { key: "ad_format_type", label: "Formato" },
        { key: "topic", label: "Tema" }, { key: "creative_id", label: "ID anuncio" }, { key: "creative_page_url", label: "Creatividad oficial", render: (row) => row.creative_page_url ? <a className="table-link" href={text(row.creative_page_url)} target="_blank" rel="noreferrer">Abrir anuncio <ExternalLink size={13} /></a> : "—" }, { key: "snapshot_date", label: "Snapshot" },
      ]} empty="No hay creatividades registradas para este cliente ni para el fallback de agencia." />
    </SectionPanel>
  </div>;
}

function Gaps({ data }: { data: GapsDetailPayload }) {
  const all = data.rows ?? [];
  const [showAll, setShowAll] = useState(false);
  const pause = all.filter((row) => text(row.decision) === "PAUSAR_PAID");
  const evaluate = all.filter((row) => text(row.decision) === "EVALUAR");
  const savings = pause.reduce((sum, row) => sum + number(row.Costo_Mensual_Est), 0);
  const affectedUrls = new Set(all.filter((row) => ["PAUSAR_PAID", "EVALUAR"].includes(text(row.decision))).map((row) => text(row.url_clean, "")).filter(Boolean));
  const visible = showAll ? all : all.slice(0, 25);
  return <div className="detail-stack">
    <div className="detail-metric-grid">
      <DetailMetric label="Ahorro potencial" value={formatValue(savings, "currency")} note="estimado mensual" tone="danger" />
      <DetailMetric label="Pausar Paid" value={String(pause.length)} note="orgánico en pos. 1–3" />
      <DetailMetric label="Evaluar" value={String(evaluate.length)} note="orgánico en pos. 4–7" />
      <DetailMetric label="URLs afectadas" value={formatValue(affectedUrls.size, "integer")} note="sin duplicar por keyword" />
    </div>
    <SectionPanel eyebrow="Paid vs orgánico" title="Decisiones por keyword" count={all.length}>
      <div className="table-scope-note">
        <span>{showAll ? `Mostrando los ${all.length} registros` : `Top ${Math.min(25, all.length)} de ${all.length}, priorizados por decisión y costo`}</span>
        {all.length > 25 && <button className="button button-ghost" type="button" onClick={() => setShowAll((value) => !value)}>{showAll ? "Ver sólo prioridades" : `Ver todos (${all.length})`}</button>}
      </div>
      <DataTable data={visible} columns={[
        { key: "palabra_clave", label: "Keyword" }, { key: "url_clean", label: "URL" },
        { key: "Pos_Organica", label: "Pos. org.", format: "decimal" }, { key: "Sesiones_Org_30d", label: "Ses. org. 30d", format: "integer" },
        { key: "Costo_Mensual_Est", label: "Costo/mes", format: "currency" }, { key: "decision", label: "Decisión", tone: (row) => text(row.decision) === "PAUSAR_PAID" ? "accent-danger" : "accent-warning" },
      ]} empty="Sin keywords que estemos pagando y ya dominemos orgánicamente." />
    </SectionPanel>
  </div>;
}

function BacklinksKpis({ data }: { data: AuditDetailPayload }) {
  const audit = record(data.audit);
  const backlinks = record(audit.backlinks);
  const available = backlinks._disponible !== false;
  return (
    <div className="detail-stack">
      {!available && <div className="detail-notice"><Info size={17} /><span>Backlinks todavía no están ingeridos para este cliente. Los ceros no representan rendimiento.</span></div>}
      <div className="detail-metric-grid">
        <DetailMetric label="Backlinks" value={formatValue(backlinks.total, "integer")} />
        <DetailMetric label="Dominios únicos" value={formatValue(backlinks.uniqueDomains, "integer")} />
        <DetailMetric label="Dofollow" value={formatValue(backlinks.dofollow, "integer")} />
        <DetailMetric label="Dominios spam" value={formatValue(backlinks.spam, "integer")} tone={number(backlinks.spam) > 0 ? "danger" : "good"} />
      </div>
    </div>
  );
}

function BacklinksDistribution({ data }: { data: AuditDetailPayload }) {
  const audit = record(data.audit);
  const backlinks = record(audit.backlinks);
  const byAs = record(backlinks.byAS);
  const distribution = Object.entries(byAs).map(([range, count]) => ({ range, count }));
  return (
    <SectionPanel eyebrow="Calidad" title="Dominios por Authority Score">
      <DataTable data={distribution} columns={[{ key: "range", label: "Rango AS" }, { key: "count", label: "Dominios", format: "integer" }]} />
    </SectionPanel>
  );
}

function BacklinksTopAnchors({ data }: { data: AuditDetailPayload }) {
  const audit = record(data.audit);
  const backlinks = record(audit.backlinks);
  return (
    <SectionPanel eyebrow="Anchors" title="Textos más frecuentes" count={rows(backlinks.topAnchors).length}>
      <DataTable data={rows(backlinks.topAnchors)} columns={[{ key: "anchor", label: "Anchor" }, { key: "count", label: "Backlinks", format: "integer" }]} />
    </SectionPanel>
  );
}

function BacklinksTopDomains({ data }: { data: AuditDetailPayload }) {
  const audit = record(data.audit);
  const backlinks = record(audit.backlinks);
  return (
    <SectionPanel eyebrow="Referring domains" title="Principales dominios" count={rows(backlinks.topDomains).length}>
      <DataTable data={rows(backlinks.topDomains)} columns={[
        { key: "domain", label: "Dominio" }, { key: "as", label: "Authority Score", format: "integer" }, { key: "count", label: "Backlinks", format: "integer" },
      ]} />
    </SectionPanel>
  );
}

function Authority({ data }: { data: AuditDetailPayload }) {
  const authority = rows(record(data.audit).authority);
  return <SectionPanel eyebrow="Semrush" title="Authority propio vs competidores" count={authority.length}>
    <DataTable data={authority} columns={[
      { key: "domain", label: "Dominio" }, { key: "as", label: "Authority Score", format: "integer" },
      { key: "domRef", label: "Dom. ref.", format: "integer" }, { key: "backlinks", label: "Backlinks", format: "integer" },
      { key: "ips", label: "IPs", format: "integer" }, { key: "note", label: "Contexto" },
    ]} />
  </SectionPanel>;
}

function CustomRangeBar({
  range,
  onChange,
}: {
  range: SocialRange;
  onChange: (r: SocialRange) => void;
}) {
  const today = todaySantiago();
  return (
    <div
      className="custom-range-bar"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "12px",
        padding: "10px 16px",
        background: "var(--surface-2, rgba(255, 255, 255, 0.03))",
        borderRadius: "var(--radius-sm, 6px)",
        border: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
        margin: "10px 0 16px 0",
      }}
    >
      <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--muted, #94a3b8)" }}>
        Rango de fechas personalizado:
      </span>
      <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8rem", color: "var(--muted, #94a3b8)" }}>
        <span>Desde:</span>
        <input
          type="date"
          className="compact-select"
          value={range.desde ?? ""}
          max={range.hasta || today}
          onChange={(e) => onChange({ ...range, desde: e.target.value })}
        />
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8rem", color: "var(--muted, #94a3b8)" }}>
        <span>Hasta:</span>
        <input
          type="date"
          className="compact-select"
          value={range.hasta ?? ""}
          min={range.desde}
          max={today}
          onChange={(e) => onChange({ ...range, hasta: e.target.value })}
        />
      </label>
    </div>
  );
}

export function ClientDetail({ clientSlug, summary }: { clientSlug: string; summary: ReactNode }) {
  const headerSlots = usePageHeaderSlots();
  const { data: dashboardData, session } = useDashboard();
  const isClientScoped = Boolean(session?.isClientUser || session?.isClientPortal);

  const initialUrl = new URLSearchParams(window.location.search).get("url") ?? undefined;
  const [active, setActive] = useState<PrimaryTab>(initialUrl ? "seo" : "summary");
  const [activeRange, setActiveRange] = useState<SocialRange>(readStoredRange);
  const activeWindow = useMemo(() => rangeToWindow(activeRange), [activeRange]);

  const handleRangeChange = (next: SocialRange) => {
    setActiveRange(next);
    storeRange(next);
  };

  const [seoTab, setSeoTab] = useState<SeoTab>("overview");
  const [paidTab, setPaidTab] = useState<PaidTab>("pacing");
  const [paidPlatform, setPaidPlatform] = useState<string>("GADS");
  const [cache, setCache] = useState<Partial<ClientDetailPayloadMap>>({});
  const [loading, setLoading] = useState<ClientDetailSection | null>(null);
  const [error, setError] = useState<string | null>(null);

  const section: ClientDetailSection = active === "paid" && paidTab === "transparency"
    ? "transparency"
    : active === "summary" ? "gaps" : active;

  const [clientBots, setClientBots] = useState<BotMetadata[] | null>(null);

  useEffect(() => {
    if (isClientScoped) return;
    let activeEffect = true;
    fetchConversationBots(clientSlug)
      .then((data) => {
        if (activeEffect) setClientBots(data);
      })
      .catch((err) => {
        console.warn("No fue posible cargar bots para", clientSlug, err);
        if (activeEffect) setClientBots([]);
      });
    return () => {
      activeEffect = false;
    };
  }, [clientSlug, isClientScoped]);

  const hasBots = Boolean(clientBots && clientBots.length > 0);

  const [hasYoutube, setHasYoutube] = useState(false);
  useEffect(() => {
    setHasYoutube(false);
    if (isClientScoped) return;
    let activeEffect = true;
    fetchSocialYoutube(clientSlug)
      .then((data) => activeEffect && setHasYoutube(data.canales.length > 0))
      .catch(() => undefined);
    return () => {
      activeEffect = false;
    };
  }, [clientSlug, isClientScoped]);

  useEffect(() => {
    if (active === "audiencias" && !hasYoutube) setActive("summary");
  }, [active, hasYoutube]);
  const theme = useDocumentTheme();

  const clientHealth = dashboardData?.health[clientSlug];

  const paidPlatforms = useMemo(() => {
    const paid = cache.paid as PaidDetailPayload | undefined;
    const fromPacing = (paid?.pacing ?? []).map((row) => text(row.platform).toUpperCase());
    const fromDaily = (paid?.daily ?? []).map((row) => text(row.platform).toUpperCase());
    const fromLifetime = (paid?.lifetime ?? []).map((row) => text(row.platform).toUpperCase());
    const fromCampaigns = (paid?.dailyCampaigns ?? []).map((row) => text(row.platform).toUpperCase());
    const fromHealth = (clientHealth?.paidPlatforms ?? []).map((item) => text(item.platform).toUpperCase());
    const unique = [...new Set([...fromPacing, ...fromDaily, ...fromLifetime, ...fromCampaigns, ...fromHealth])].filter(Boolean);
    const prio: Record<string, number> = { GADS: 1, META: 2, LINKEDIN: 3, TIKTOK: 4, PINTEREST: 5 };
    return unique.sort((a, b) => (prio[a] ?? 99) - (prio[b] ?? 99));
  }, [cache.paid, clientHealth]);

  useEffect(() => {
    if (paidPlatforms.length > 0 && paidPlatform !== "ALL" && !paidPlatforms.includes(paidPlatform)) {
      setPaidPlatform(paidPlatforms.includes("GADS") ? "GADS" : paidPlatforms[0]);
    }
  }, [paidPlatforms, paidPlatform]);

  useEffect(() => {
    setCache({});
    if (initialUrl) {
      setActive("seo");
    }
    clearUrlDetailCache();
  }, [clientSlug, initialUrl]);

  /** Reemplaza (o agrega) la auditoría de esa plataforma/cuenta dentro del
   *  payload Paid ya cacheado, en vez de mantenerla en estado local del hijo. */
  const mergePaidAudit = (audit: GertrudisAuditPayload) => {
    setCache((current) => {
      const paid = current.paid;
      if (!paid) return current;
      const platform = text(audit.platform).toUpperCase();
      const accountId = text(audit.accountId, "");
      const rest = (paid.gertrudisAudits ?? []).filter(
        (item) => !(text(item.platform).toUpperCase() === platform && text(item.accountId, "") === accountId),
      );
      return { ...current, paid: { ...paid, gertrudisAudits: [audit as DataRecord, ...rest] } };
    });
  };

  useEffect(() => {
    if (section === "whatsapp" || section === "audiencias") return;
    const cached = cache[section];
    if (cached && !(section === "seo" || section === "paid")) return;
    if (cached && record(cached).window && text(record(record(cached).window).window) === activeWindow) return;
    let cancelled = false;
    setLoading(section);
    setError(null);
    void fetchClientDetail(clientSlug, section, activeWindow)
      .then((payload) => {
        if (!cancelled) setCache((current) => ({ ...current, [section]: payload }));
      })
      .catch((cause) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "No fue posible cargar este tab.");
      })
      .finally(() => { if (!cancelled) setLoading(null); });
    return () => { cancelled = true; };
  }, [clientSlug, section, activeWindow, cache]);

  const retry = () => {
    setCache((current) => {
      const next = { ...current };
      delete next[section];
      return next;
    });
    setError(null);
  };

  const current = cache[section];

  const visibleTabs = useMemo(() => {
    return primaryTabs.filter((tab) => (tab.id !== "whatsapp" || hasBots) && (tab.id !== "audiencias" || hasYoutube));
  }, [hasBots, hasYoutube]);

  const primaryTabsNav = <div className="client-primary-tabs" role="tablist">
    {visibleTabs.map((tab) => (
      <button
        type="button"
        role="tab"
        aria-selected={active === tab.id}
        className={active === tab.id ? "is-active" : ""}
        key={tab.id}
        onClick={() => setActive(tab.id)}
      >
        {tab.label}
      </button>
    ))}
  </div>;

  return (
    <UpdatingContext.Provider value={loading === section}>
      <section className="client-detail-shell">
    {headerSlots.left && createPortal(primaryTabsNav, headerSlots.left)}

    {active === "paid" && (
      <SubTabs
        items={[
          { id: "pacing", label: "Resumen Paid" },
          { id: "transparency", label: "Creatividades" },
        ]}
        active={paidTab}
        onChange={setPaidTab}
      />
    )}

    {active === "seo" && (
      <SubTabs
        items={[
          { id: "overview", label: "Resumen SEO" },
          { id: "opportunities", label: "Oportunidades" },
          { id: "explore", label: "Explorar" },
        ]}
        active={seoTab}
        onChange={setSeoTab}
      />
    )}

    {(active === "seo" || active === "paid") && activeRange.preset === "custom" && (
      <CustomRangeBar range={activeRange} onChange={handleRangeChange} />
    )}

    {headerSlots.right && createPortal(
      <>
        {active === "paid" && paidTab === "pacing" && paidPlatforms.length > 0 && (
          <div className="platform-filter">
            {paidPlatforms.length > 1 && (
              <button
                type="button"
                data-platform="ALL"
                className={paidPlatform === "ALL" ? "is-active" : ""}
                onClick={() => setPaidPlatform("ALL")}
                key="ALL"
              >
                <span className="platform-dot" style={{ background: "var(--muted)" }} />
                TODAS
              </button>
            )}
            {paidPlatforms.map((item) => (
              <button
                type="button"
                data-platform={item}
                className={paidPlatform === item ? "is-active" : ""}
                onClick={() => setPaidPlatform(item)}
                key={item}
              >
                <span className="platform-dot" />
                {item}
              </button>
            ))}
          </div>
        )}

        {(active === "seo" || (active === "paid" && paidTab === "pacing")) && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            {loading === section && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 10px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "var(--accent, #38bdf8)",
                  background: "rgba(56, 189, 248, 0.12)",
                  border: "1px solid rgba(56, 189, 248, 0.3)",
                }}
              >
                <Loader2 className="animate-spin" size={13} />
                Actualizando…
              </span>
            )}
            <select
              className="compact-select"
              aria-label="Ventana temporal"
              value={activeRange.preset}
              disabled={loading === section}
              onChange={(event) => {
                const preset = event.target.value as RangePreset;
                handleRangeChange(
                  preset === "custom"
                    ? { preset, desde: activeRange.desde, hasta: activeRange.hasta }
                    : { preset }
                );
              }}
            >
              {(Object.keys(PRESET_LABELS) as RangePreset[]).map((key) => (
                <option key={key} value={key}>
                  {PRESET_LABELS[key]}
                </option>
              ))}
            </select>
          </div>
        )}
      </>,
      headerSlots.right,
    )}

    {active === "summary" && <div className="detail-stack account-summary-tab">
      {summary}
      <div className="section-divider-heading"><span className="eyebrow">SEO ↔ Paid</span><h2>Oportunidades cruzadas</h2><p>Keywords donde el posicionamiento orgánico permite revisar inversión Paid o exige una evaluación conjunta.</p></div>
    </div>}

    {active === "audiencias" && hasYoutube && <AudienciaYoutube clientSlug={clientSlug} theme={theme} />}

    {loading === section && !current && <div className="detail-loading"><span /><p>Cargando {active === "summary" ? "oportunidades cruzadas" : `datos de ${active}`}…</p></div>}
    {error && <div className="detail-error"><p>{error}</p><button type="button" onClick={retry}><RefreshCw size={14} /> Reintentar</button></div>}
    {!error && current && <>
      {active === "summary" && <Gaps data={current as GapsDetailPayload} />}
      {active === "seo" && <SeoDetail data={current as SeoDetailPayload} clientSlug={clientSlug} initialUrl={initialUrl} tab={seoTab} onTabChange={setSeoTab} onHitoCreated={retry} />}
      {active === "paid" && paidTab === "pacing" && <PaidPacing data={current as PaidDetailPayload} clientSlug={clientSlug} onAuditGenerated={mergePaidAudit} activePlatform={paidPlatform} onPlatformChange={setPaidPlatform} onHitoCreated={retry} />}
      {active === "paid" && paidTab === "transparency" && <Transparency data={current as TransparencyDetailPayload} />}
      {active === "whatsapp" && (
        clientBots && clientBots.length === 0 ? (
          <div
            className="conversations-empty-container"
            style={{
              padding: "56px 24px",
              textAlign: "center",
              background: "var(--bg-surface, #111827)",
              borderRadius: "8px",
              border: "1px solid var(--border-subtle, rgba(255, 255, 255, 0.08))",
              margin: "16px 0",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                padding: "16px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.05)",
                marginBottom: "16px",
                color: "var(--text-muted, #9ca3af)",
              }}
            >
              <Bot size={44} />
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 600, margin: "0 0 8px", color: "var(--text-primary, #f9fafb)" }}>
              Sin bots conectados
            </h3>
            <p style={{ color: "var(--text-muted, #9ca3af)", margin: 0, fontSize: "14px", maxWidth: "480px", marginInline: "auto" }}>
              Este cliente no posee bots configurados en <code style={{ color: "var(--text-primary)" }}>dim_bot</code>.
            </p>
          </div>
        ) : (
          <ClientConversationsTab clientSlug={clientSlug} initialBots={clientBots ?? []} />
        )
      )}
    </>}
      </section>
    </UpdatingContext.Provider>
  );
}

